import './rc-switch.css'

import { Checkbox, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { SvgIcon } from '@progress/kendo-react-common'
import clsx from 'clsx'
import _ from 'lodash'
import Switch from 'rc-switch'
import {
    ChangeEvent,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'

import { Scrollbars } from 'react-custom-scrollbars-2'
import { useMediaQuery } from 'react-responsive'
import useMeasure from 'react-use-measure'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { CUSTOMER_TYPES } from '../../consts'
import {
    fetchPartInfo as fetchInvtPartInfo,
    fetchPartInfo,
    FetchPartInfoReturnPayload,
} from '../../features/inventory/inventoryAPI'
import { LOADING_STATES } from '../../features/meta/metaSlice'
import {
    EstimatedDeliveryReturnPayload,
    fetchParts,
    fetchPartsAvailability,
    FetchPriceReturnPayload,
    FetchRebateAnalyticsReturnPayload,
    FetchRebateStatusReturnPayload,
    fetchXrefInfo,
    makeEstimatedDeliveryParams,
    PartsAvailabilityReturnPayload,
    PartSearchInfo,
    RebateHeaderCustView,
    useEstimatedDelivery,
    usePricingStatistics,
} from '../../features/order/orderAPI'
import {
    deleteLine,
    getOrderMiscInfo,
    // makeEstimatedDeliveryParams,
    markRowInvalid,
    markRowValid,
    PayloadOrderDetailViewRow,
    PayloadOrderHeaderViewRow,
    QueryInfo,
    setAddLineItemFromBOMLoading,
    updateAvailabilityMode,
    updateLine,
    useRebateAnalytics,
} from '../../features/order/orderSlice'

import { DatePicker } from '../../GlobalComponents/DateInput/DateInput'
import {
    ConfirmModal,
    Dialog,
    TextAreaModal,
} from '../../GlobalComponents/GenericModals/GenericModals'
import { getCookie } from '../../GlobalComponents/GlobalFunctions'
import { EditPencil } from '../../GlobalComponents/_icons/Icons'
import {
    AWCButton,
    BUTTON_TYPES,
    MoneyInput,
    OnBlurFastQuoteInput,
    ToggledInput,
} from '../../GlobalComponents/Inputs/Inputs'
import { TextOverflow } from '../../GlobalComponents/_misc/TextOverflow'
import { getBaseURL } from '../../GlobalComponents/URLs'
import { getNumDecimalPlaces } from '../../utils'
import { HoverIcon } from '../../GlobalComponents/_icons/HoverIcon'
import styles from './LineItems.module.scss'
import { AdvancedPartSearchModal } from './AdvancedPartSearchModal'

// import {
//     SortableOnDragEndEvent,
//     SortableOnDragOverEvent,
//     SortableOnDragStartEvent,
//     SortableOnNavigateEvent,
// } from '@progress/kendo-react-sortable'
const formatMoney = (num: number | undefined) => {
    if (num === undefined) return ''
    let showDecimals = Math.max(2, getNumDecimalPlaces(num))
    showDecimals = Math.min(showDecimals, 4)
    return num.toFixed(showDecimals)
}

function getBusinessWeeksCount(startDate: Date, endDate: Date) {
    let count = 0
    const curDate = new Date(startDate.getTime())
    while (curDate <= endDate) {
        const dayOfWeek = curDate.getDay()
        if (dayOfWeek !== 0 && dayOfWeek !== 6) count++
        curDate.setDate(curDate.getDate() + 1)
    }
    const businessWeeks = Math.ceil(count / 5)
    return businessWeeks === 0 ? 1 : businessWeeks
}

interface LineItemsProps {
    reviewingQuoteOutput: boolean
    accountCode: string
    orderDetail?: PayloadOrderDetailViewRow[]
    orderHeader?: PayloadOrderHeaderViewRow
    noNotes?: boolean
    isPreview?: boolean
    //TODO probably need customertype
}

const App = forwardRef((props: LineItemsProps, ref) => {
    const { reviewingQuoteOutput, accountCode, noNotes, isPreview } = props
    const dispatch = useAppDispatch()

    const [focusedLine, setFocusedLine] = useState<string>('')
    useImperativeHandle(ref, () => ({
        focusLine: async (lineItemNumber: string) => {
            let lineRef = lineRefs.current[lineItemNumber]
            if (!lineRef) return
            // use this to tell when we've actually scrolled it on screen
            const intersectionObserver = new IntersectionObserver(
                (entries) => {
                    let [entry] = entries
                    if (entry.isIntersecting) {
                        setTimeout(() => setFocusedLine(lineItemNumber), 200)
                        setTimeout(() => setFocusedLine(''), 2000)
                    }
                },
                { threshold: 1.0 }
            )
            intersectionObserver.observe(lineRef)

            lineRef.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // wait a bit and stop observing
            setTimeout(() => intersectionObserver.unobserve(lineRef), 2000)
        },
    }))

    // const orderHeader = useAppSelector((state) => state.order.orderHeader)
    let orderDetailState = useAppSelector((state) =>
        state.order.orderHeader[0] ? state.order.orderHeader[0].OrderDetailView : []
    )
    let orderDetail = props.orderDetail || orderDetailState

    let orderHeaderState = useAppSelector((state) => state.order.orderHeader[0])
    let orderHeader = props.orderHeader || orderHeaderState

    // const [events, setEvents] = useState<Array<any>>([])

    const element = useRef<HTMLUListElement | HTMLUListElement>(null)
    const lineRefs = useRef<Record<string, HTMLDivElement>>({})
    useEffect(() => {
        if (element.current) element.current.scrollTop = element.current.scrollHeight
    })

    const [showLoading, setShowLoading] = useState(false)
    const addLineItemFromBOMLoading = useAppSelector(
        (state) => state.order.addLineItemFromBOMLoading
    )
    const [addLineItemsFromBOMDone, setAddLineItemsFromBOMDone] = useState(false)

    const saveBlockingProcessPendingCount = useAppSelector(
        (state) => state.order.saveBlockingProcessPendingCount
    )

    useEffect(() => {
        if (addLineItemFromBOMLoading === LOADING_STATES.INIT) return

        if (addLineItemFromBOMLoading === LOADING_STATES.LOADING) {
            if (showLoading && saveBlockingProcessPendingCount === 0) {
                if (!addLineItemsFromBOMDone) {
                    // Once all BOM line items are added, get order misc info once
                    // setTimeout makes sure it happens after all blocking processes
                    setTimeout(() => dispatch(getOrderMiscInfo(reviewingQuoteOutput)), 0)
                    setAddLineItemsFromBOMDone(true)
                } else {
                    // Once final get order misc info is complete, hide loading veil
                    setShowLoading(false)
                    dispatch(setAddLineItemFromBOMLoading(LOADING_STATES.DONE))
                }
            } else setShowLoading(true)
        } else if (saveBlockingProcessPendingCount > 0)
            dispatch(setAddLineItemFromBOMLoading(LOADING_STATES.LOADING))
    }, [addLineItemFromBOMLoading, saveBlockingProcessPendingCount])

    return (
        <div className={styles.lineItemContainer}>
            {showLoading && (
                <div className={`centered veil ${styles.veil}`}>
                    <div className={'k-i-loading'} style={{ fontSize: 200, height: '90vh' }} />
                </div>
            )}
            <LineItemHeader reviewingQuoteOutput={reviewingQuoteOutput} noNotes={noNotes} />
            {/* ! ENABLING BREAKS ROW NEED DATE CLICK TO FOCUS DATES -- 
                      CLICKING TRIGGERS RERENDER THAT MESSES WITH INPUT! tried eating the input with stop
                      propagation but they listen to it outside of react*/}
            {/* <Sortable
                    idField={'rowId'}
                    disabledField={'disabled'}
                    data={orderDetail.map((row, index) => ({
                        ...row,
                        lineRefs,
                        index,
                        focused: focusedLine === row.LineItemNumber,
                    }))}
                    itemUI={SortableItemUI}
                    onDragStart={() => null}
                    onDragOver={() => null}
                    onDragEnd={() => null}
                    onNavigate={() => null}
                    // onDragStart={onDragStart}
                    // onDragOver={onDragOver}
                    // onDragEnd={onDragEnd}
                    // onNavigate={onNavigate}
                /> */}

            {orderDetail.map((row, index) => {
                if (row.temp && reviewingQuoteOutput) {
                    return <></>
                } else {
                    let dataItem = {
                        ...row,
                        lineRefs,
                        index,
                        focused: focusedLine === row.LineItemNumber,
                        accountCode,
                        orderHeader,
                        noNotes,
                        last: orderDetail.length === index + 1,
                    }
                    return (
                        <SortableItemUI
                            dataItem={dataItem}
                            key={row.rowId}
                            reviewingQuoteOutput={reviewingQuoteOutput}
                            isPreview={isPreview}
                        />
                    )
                }
            })}
        </div>
    )
})
export default App

const removeVendorID = (raw: string) => {
    let search = /^\s*\w{2}\s(.*)/.exec(raw)
    return search ? search[1] : raw
}

export const getCustomerType = (typeId: number) => {
    return Object.values(CUSTOMER_TYPES).find((row) => row.typeId === typeId)!.typeText
}

export const getSpecificPartInfo = async (
    partSearchInfo: PartSearchInfo,
    accountCode: string
): Promise<PartNumberChangeInfo> => {
    let partInfoPromise = fetchPartInfo({
        PartKey: +partSearchInfo.Value,
        SearchMfgPartNumberFallback: false,
    })
    let partInfo = await partInfoPromise

    let partXref = ''
    if (accountCode !== '') {
        let partXrefPromise = fetchXrefInfo({
            Partkey: partSearchInfo.Value,
            CustomerAccountCode: accountCode,
        })
        partXref = await partXrefPromise
    }

    return { PartInfo: partInfo!, PartXRefInfo: partXref, PartSearchInfo: partSearchInfo }
}
interface PartNumberFieldProps {
    isCurrentPart: boolean
    partLoading: boolean
    onChange(partSearchInfo: PartNumberChangeInfo | null): void
    rowId: string
    testId: string
    accountCode: string
    partNumber: string
    orderDetailRow: PayloadOrderDetailViewRow
}

const PartNumberField = (props: PartNumberFieldProps) => {
    const { isCurrentPart, partLoading, onChange, rowId, accountCode } = props
    const [parts, setParts] = useState<{
        data: Array<PartSearchInfo>
        loading: boolean
        search: string
    }>({ data: [], loading: false, search: '' })
    let invalidRows = useAppSelector((state) => state.order.invalidLines)
    let showInfo = isCurrentPart && !invalidRows[rowId]?.partNumber
    const [active, setActive] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingPart, setLoadingPart] = useState(false)
    // const [selectedFullPart, setSelectedFullPart] = useState<PartNumberChangeInfo | null>(null)
    const orderDetailRowState = useAppSelector((state) =>
        state.order.orderHeader[0].OrderDetailView.find((row) => row.rowId === rowId)
    )
    const orderDetailRow = props.orderDetailRow || orderDetailRowState

    const addLineItemFromBOMLoading = useAppSelector(
        (state) => state.order.addLineItemFromBOMLoading
    )

    const [replacementMfgPartNumber, setReplacementMfgPartNumber] = useState<string>(
        orderDetailRow.metaInfo?.partInfo?.ReplacedByMfgPartnumber ?? ''
    )

    const [preferredPartNumberModalOpen, setPreferredPartNumberModalOpen] = useState<boolean>(false)

    const [partNumberPreferenceSelected, setPartNumberPreferenceSelected] = useState<boolean>(false)

    const [searchValue, setSearchValue] = useState(
        orderDetailRow.metaInfo?.partInfo?.PartNumber || ''
    )

    let partInfo = orderDetailRow.metaInfo?.partInfo
    useEffect(() => {
        if (
            orderDetailRow.metaInfo?.partInfo?.ReplacedByMfgPartnumber &&
            !orderDetailRow.temp &&
            !partNumberPreferenceSelected
        ) {
            setReplacementMfgPartNumber(orderDetailRow.metaInfo.partInfo.ReplacedByMfgPartnumber)
            setPreferredPartNumberModalOpen(true)
        }

        //when row jumps up since temp rows don't have row ids
        if (
            orderDetailRow &&
            orderDetailRow.PartNumber &&
            !partNumberPreferenceSelected &&
            !advancedPartSearch
        ) {
            setSearchValue(orderDetailRow.PartNumber)
        }
    }, [orderDetailRow])

    const [advancedPartSearch, setAdvancedPartSearch] = useState<string>('')

    const inputRef = useRef<any>()

    useEffect(() => {
        if (advancedPartSearch) {
            setSearchValue(advancedPartSearch)
            inputRef.current && inputRef.current.blur()
        }
    }, [advancedPartSearch])

    const dispatch = useAppDispatch()
    const tryToGetPart = async (searchValue: string): Promise<PartNumberChangeInfo | null> => {
        let partInfoPromise = fetchPartInfo({
            PartNumber: searchValue,
            SearchMfgPartNumberFallback: true,
        })
        let partInfo = await partInfoPromise
        if (!partInfo) return null
        let partXrefPromise = fetchXrefInfo({
            Partkey: partInfo.Partkey.toString(),
            CustomerAccountCode: accountCode,
        })
        let partXref = await partXrefPromise

        return {
            PartInfo: partInfo,
            PartXRefInfo: partXref,
            PartSearchInfo: null,
        }
    }

    const getParts = useCallback(
        _.debounce(
            async (search: string, controller: AbortController) => {
                setLoading(true)
                setParts({ search: search, loading: true, data: [] })
                let parts = await fetchParts(
                    { searchValue: search, accountCode: accountCode },
                    controller
                )
                if (parts) {
                    setParts({ search: search, loading: false, data: parts.d })
                }
                setLoading(false)
            },
            500,
            { trailing: true }
        ),
        []
    )
    const handleKeyDown = useCallback((event: any) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.target.blur()
        }
    }, [])
    useEffect(() => {
        const controller = new AbortController()
        if (
            partInfo &&
            searchValue !== partInfo.PartNumber &&
            searchValue !== removeVendorID(partInfo.PartNumber)
        ) {
            onChange(null)
        }
        if (searchValue && addLineItemFromBOMLoading !== LOADING_STATES.LOADING) {
            getParts(searchValue, controller)
        }

        return () => {
            controller.abort()
        }
    }, [searchValue])

    useEffect(() => {
        if (
            !active &&
            searchValue &&
            (!partInfo ||
                (searchValue !== partInfo.PartNumber &&
                    searchValue !== removeVendorID(partInfo.PartNumber)))
        ) {
            setLoadingPart(true)
            tryToGetPart(searchValue.trim())
                .then((changeInfo) => {
                    // might be loading part from existing order and don't want to trigger change
                    changeInfo &&
                        changeInfo.PartInfo.PartNumber !== orderDetailRow.PartNumber &&
                        onChange(changeInfo)
                    changeInfo && dispatch(markRowValid({ rowId, field: 'partNumber' }))

                    // changeInfo && setSearchValue(removeVendorID(changeInfo.PartInfo.PartNumber))
                    if (!changeInfo) {
                        if (rowId) dispatch(markRowInvalid({ rowId, field: 'partNumber' }))
                        // TODO make sure this is ok to remove -- having a bunch of invalid lines
                        // else dispatch(addInvalidLine({ lineItemNumber }))
                    }
                })
                .finally(() => {
                    setLoadingPart(false)
                })
        }
    }, [active, searchValue])
    useEffect(() => {
        if (
            partInfo &&
            active &&
            inputRef &&
            inputRef.current &&
            partInfo.PartNumber === searchValue
        ) {
            // weird but we're changing the value and rerender will drop select
            setTimeout(() => inputRef.current.select(), 50)
        }
    }, [active, partInfo])

    const [advancedSearchModalOpen, setAdvancedSearchModalOpen] = useState<boolean>(false)

    const closeAdvancedSearchModal = () => {
        setAdvancedSearchModalOpen(false)
    }

    const productCount = parts.data.find((row) => row.Source === 'ProductCount')

    const ListboxComponent = (props: any) => {
        return (
            <>
                {productCount && parseInt(productCount.Value) > 4 && (
                    <div
                        style={{
                            padding: 10,
                            display: 'flex',
                            backgroundColor: '#F2F2F2',
                            fontWeight: 500,
                            fontSize: 12,
                            justifyContent: 'space-between',
                        }}
                        onMouseDown={(event) => {
                            event.preventDefault()
                        }}
                    >
                        <span>{productCount.Value} results found</span>
                        <span
                            style={{ color: 'var(--linkblue)', cursor: 'pointer' }}
                            onClick={() => {
                                setAdvancedSearchModalOpen(true)
                            }}
                        >
                            Show more {'>'}
                        </span>
                    </div>
                )}
                <ul {...props} />
            </>
        )
    }

    return (
        <div data-testid={props.testId} style={{ position: 'relative' }}>
            {!active &&
                invalidRows[rowId]?.partNumber &&
                !loadingPart &&
                searchValue &&
                !partLoading && (
                    <div style={{ color: 'red', position: 'absolute', top: -19 }}>
                        {' '}
                        No exact matches found.{' '}
                    </div>
                )}
            <Autocomplete
                ListboxComponent={ListboxComponent}
                options={parts.data.filter(
                    (data: PartSearchInfo) => data.Source !== 'ProductCount'
                )}
                getOptionLabel={(option) => option.Name}
                onChange={async (e, value) => {
                    if (typeof value !== 'string') {
                        if (!value) onChange(value)
                        else {
                            let changeInfo = await getSpecificPartInfo(value, accountCode)
                            setSearchValue(changeInfo.PartInfo.PartNumber)
                            onChange(changeInfo)
                        }
                    } else {
                        if (parts.data.length === 1) {
                            let changeInfo = await getSpecificPartInfo(parts.data[0], accountCode)
                            setSearchValue(changeInfo.PartInfo.PartNumber)
                            onChange(changeInfo)
                        } else {
                            setSearchValue(value)
                        }
                    }
                }}
                onInputChange={(e, value) => {
                    setSearchValue(value.toUpperCase())
                }}
                filterOptions={(options, state) => {
                    return options
                }}
                loading={loading}
                freeSolo
                inputValue={
                    !active && partInfo && partInfo.PartNumber === searchValue
                        ? removeVendorID(partInfo.PartNumber)
                        : searchValue
                }
                value={null}
                renderOption={(option, state) => {
                    let divs = [...option.Markup.matchAll(/<div>(.*?)<\/div>/g)]
                    return (
                        <div>
                            <div style={{ fontWeight: 500, fontSize: 12 }}>{divs[0][1]}</div>
                            {divs[1] && <div style={{ fontSize: 10 }}>{divs[1][1]}</div>}
                        </div>
                    )
                }}
                renderInput={(params) => {
                    return (
                        <div style={{ position: 'relative' }}>
                            <TextField
                                {...{
                                    ...params,

                                    InputProps: {
                                        ...params.InputProps,
                                        endAdornment: undefined,
                                        style: {
                                            paddingBottom: 0,
                                            paddingLeft: 8,
                                            alignSelf: 'flex-end',
                                        },
                                    },
                                    inputProps: {
                                        ...params.inputProps,
                                        style: partInfo
                                            ? { paddingBottom: 0, textTransform: 'uppercase' }
                                            : { textTransform: 'uppercase' },
                                    },
                                    InputLabelProps: {
                                        ...params.InputLabelProps,
                                        shrink: true,
                                    },
                                }}
                                variant='outlined'
                                // onChange={partnumberOnChange}
                                onFocus={(e) => {
                                    setActive(true)
                                    e.target.select()
                                }}
                                onBlur={() => {
                                    setActive(false)
                                }}
                                onKeyDown={handleKeyDown}
                                inputRef={inputRef}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 3,
                                    left: 14,
                                    fontSize: 14,
                                    fontWeight: 300,
                                }}
                            >
                                {showInfo && partInfo && partInfo.MfgName}
                                {showInfo && partInfo && ` (${partInfo.VendorID})`}
                            </div>
                        </div>
                    )
                }}
            />
            {showInfo && (
                <>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'max-content 1fr',
                            marginTop: 18,
                            fontSize: 'var(--font-size-s)',
                            marginLeft: 6,
                            gap: 4,
                        }}
                        className={styles.partNumberInfo}
                    >
                        <div style={{ alignSelf: 'center' }}>
                            {showInfo && partInfo && 'Manufacturer #:'}
                        </div>
                        <div style={{ alignSelf: 'center' }}>
                            {/* {showInfo && partInfo && partInfo.MfgPartNumber} */}
                            {showInfo && partInfo && (
                                <TextOverflow
                                    text={partInfo.MfgPartNumber || ''}
                                    font={'14px roboto'}
                                    maxWidth={220}
                                    bubbleMinWidth={150}
                                />
                            )}
                        </div>
                        {/* height 25 to make input not jump */}
                        <div
                            style={{
                                alignSelf: 'center',
                                // height: '20px',
                                lineHeight: '20px',
                            }}
                        >
                            {showInfo && partInfo && 'Customer #: '}{' '}
                        </div>
                        {partInfo && (
                            <ToggledInput
                                onChange={(newVal) => {
                                    // updateLine({
                                    //     rowId: rowId,
                                    //     SymbolNumber: newVal,
                                    // })
                                    dispatch(
                                        updateLine({
                                            rowId: rowId,
                                            SymbolNumber: newVal,
                                        })
                                    )
                                }}
                                value={orderDetailRow?.SymbolNumber || ''}
                                inactiveTextStyle={{
                                    lineHeight: '20px',
                                }}
                                activeTextStyle={{
                                    height: '20px',
                                    background: 'initial',
                                }}
                            />
                        )}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                        }}
                    >
                        <div style={{ marginRight: 3, marginLeft: 4 }}>
                            <HoverIcon
                                style={{ display: 'block' }}
                                growUp={true}
                                ignoreClick={true}
                                renderIcon={() => (
                                    <Checkbox
                                        color='primary'
                                        style={{ fontSize: '1.3rem' }}
                                        disabled={!orderDetailRow.metaInfo?.dataSheets?.length}
                                        checked={orderDetailRow.includeDataSheets}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                            // updateLine({
                                            //     rowId: rowId,
                                            //     includeDataSheets: event.target.checked,
                                            // })
                                            dispatch(
                                                updateLine({
                                                    rowId: rowId,
                                                    includeDataSheets: event.target.checked,
                                                })
                                            )
                                        }}
                                    />
                                )}
                            >
                                {(closeBubble: () => void) =>
                                    !orderDetailRow.metaInfo?.dataSheets?.length && (
                                        <div
                                            className={styles.identifierBubble}
                                            style={{
                                                width: 'max-content',
                                                maxWidth: 300,
                                            }}
                                        >
                                            We have no data sheets for this part
                                        </div>
                                    )
                                }
                            </HoverIcon>
                        </div>
                        <div
                            style={{
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 300,
                                margin: 'auto 0px',
                            }}
                        >
                            Include Data Sheets
                        </div>
                    </div>
                </>
            )}
            {preferredPartNumberModalOpen && (
                <Dialog
                    title={'Use Preferred Part Number'}
                    onCancel={() => {
                        setPreferredPartNumberModalOpen(false)
                        setReplacementMfgPartNumber('')
                        setPartNumberPreferenceSelected(true)
                    }}
                    onConfirm={() => {
                        setPreferredPartNumberModalOpen(false)
                        setReplacementMfgPartNumber('')
                        setPartNumberPreferenceSelected(true)
                        setSearchValue(replacementMfgPartNumber.toUpperCase())
                    }}
                    cancelText='No'
                    confirmText='Yes'
                >
                    <div style={{ marginTop: 30 }}>
                        <span>
                            Line <b>{orderDetailRow.LineItemNumber}</b> has been replaced by{' '}
                            <b>{replacementMfgPartNumber}</b>.
                        </span>
                        <span> Do you want to use the replacement part?</span>
                    </div>
                </Dialog>
            )}
            {advancedSearchModalOpen && (
                <AdvancedPartSearchModal
                    close={closeAdvancedSearchModal}
                    value={searchValue}
                    setSearchValue={setAdvancedPartSearch}
                />
            )}
        </div>
    )
}
// const RowHandle = (attributes: any) => {
//     return (
//         <div
//             style={{
//                 width: 'var(--handleWidth)',
//                 position: 'absolute',
//                 left: -20,
//                 height: '100%',
//                 display: 'flex',
//                 alignItems: 'center',
//             }}
//             {...attributes}
//         >
//             <div
//                 style={{
//                     display: 'flex',
//                     width: 'var(--handleWidth)',
//                     justifyContent: 'space-between',
//                     padding: 6,
//                     boxSizing: 'border-box',
//                 }}
//             >
//                 <div
//                     style={{
//                         display: 'flex',
//                         height: 24,
//                         justifyContent: 'space-evenly',
//                         flexDirection: 'column',
//                     }}
//                 >
//                     <div className={styles.circle} />
//                     <div className={styles.circle} />
//                     <div className={styles.circle} />
//                 </div>
//                 <div
//                     style={{
//                         display: 'flex',
//                         height: 24,
//                         justifyContent: 'space-evenly',
//                         flexDirection: 'column',
//                     }}
//                 >
//                     <div className={styles.circle} />
//                     <div className={styles.circle} />
//                     <div className={styles.circle} />
//                 </div>
//             </div>
//         </div>
//     )
// }

// TODO typescriptify away this generic
interface PricePopupParams {
    //priceStatistics: FetchPricingStatisticsReturnPayload
    partKey: number
    priceInfo: FetchPriceReturnPayload
    unitPrice?: number
    accountCode: string
    customerType?: number
}
const PricePopup = (props: PricePopupParams) => {
    const { partKey, priceInfo, unitPrice, accountCode, customerType } = props
    // const { priceStatistics, priceInfo, unitPrice, accountCode, customerType } = props
    const [
        priceStatistics,
        psLoading,
        //psError
    ] = usePricingStatistics({
        CustomerAccountCode: accountCode,
        Partkey: partKey,
    })
    const [showingMore, setShowingMore] = useState(false)
    if (!priceStatistics || psLoading) return <></>

    let customerSalesHistory = priceStatistics.SalesHistory.filter(
        (row) => row.CustomerAccountCode.trim() === accountCode.trim()
    )
    let lastGp =
        customerSalesHistory[0]?.GrossProfit != null
            ? `${customerSalesHistory[0].GrossProfit}%`
            : null
    let discount = !unitPrice
        ? 'N/A'
        : priceStatistics.List === 0
        ? 'N/A'
        : `${((100 * (priceStatistics.List - unitPrice)) / priceStatistics.List).toFixed(2)}%`

    let lastSellDate = priceStatistics.SalesHistory[0]?.TransactionDate
        ? new Date(priceStatistics.SalesHistory[0].TransactionDate)
        : new Date()
    let lastChangeDate = new Date(priceStatistics.PricingEffectiveDate)
    let soldBeforeChange = lastSellDate < lastChangeDate
    let lastSell =
        customerSalesHistory[0]?.UnitPrice !== undefined ? customerSalesHistory[0].UnitPrice : null

    // let numDecimals = Math.max(
    //     2,
    //     lastSell ? getNumDecimalPlaces(lastSell) : 0,
    //     getNumDecimalPlaces(priceStatistics.List),
    //     // table most digits
    //     priceStatistics.SalesHistory.reduce(
    //         (prev, current) =>
    //             Math.max(
    //                 prev,
    //                 getNumDecimalPlaces(current.UnitPrice),
    //                 getNumDecimalPlaces(current.UnitRebate)
    //             ),
    //         0
    //     )
    // )

    return (
        <div
            className={styles.identifierBubble}
            style={{ fontSize: 14, fontWeight: 400, width: 'max-content' }}
        >
            <div style={{ position: 'relative' }}>
                <div>
                    <div className={styles.pricePopupGrid}>
                        <div>CPA:</div>{' '}
                        <div
                            onClick={() =>
                                window.open(
                                    `${getBaseURL()}masterclient/main.aspx?OpenDialog=CustomerContract&CustAcct=${accountCode}&ContractNum=${
                                        priceInfo.CustomerContractNumber
                                    }`
                                )
                            }
                            style={{
                                color: 'var(--linkblue)',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                            }}
                        >
                            {priceInfo.CustomerContractNumber}
                        </div>
                        <div></div>
                        <div style={{ fontStyle: 'italic', fontSize: 12 }}>
                            Link will open .Net in a new tab. Select Fast Quote tab to return to
                            Fast Quote.
                        </div>
                        <div>Discount:</div> <div>{discount}</div>
                        <div>List Price (Updated):</div>{' '}
                        <div>
                            $ {formatMoney(priceStatistics.List)} (
                            {new Date(priceStatistics.PricingEffectiveDate).toLocaleDateString()})
                        </div>
                        <div>Average Sell:</div>{' '}
                        <div>
                            {priceStatistics.AverageSellPrice !== null
                                ? `$ ${formatMoney(priceStatistics.AverageSellPrice)}`
                                : 'N/A'}{' '}
                            ({getCustomerType(customerType!)})
                        </div>
                        <div>Last Sell:</div>{' '}
                        <div>
                            {soldBeforeChange && '*'}
                            {lastSell !== null ? `$ ${formatMoney(lastSell)}` : 'N/A'}
                        </div>
                        <div>Last GP:</div> <div>{lastGp !== null ? lastGp : 'N/A'}</div>
                        {soldBeforeChange && (
                            <>
                                <div></div>
                                <div>*Before price increase</div>
                            </>
                        )}
                    </div>
                    <div className='a-divider' style={{ marginTop: 10, marginBottom: 10 }} />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: 10,
                            width: '100%',
                        }}
                    >
                        {showingMore ? (
                            <AWCButton
                                type={BUTTON_TYPES.LINK}
                                onClick={() => setShowingMore(false)}
                            >
                                Hide
                            </AWCButton>
                        ) : customerType && customerType !== 16 ? ( // 16 is Uncertain
                            <AWCButton
                                type={BUTTON_TYPES.LINK}
                                onClick={() => setShowingMore(true)}
                            >
                                Market Sell Price Data
                            </AWCButton>
                        ) : (
                            <div>
                                Customer Type is uncertain, update to improve pricing comparison
                            </div>
                        )}
                    </div>

                    {showingMore && (
                        <div
                            className={styles.identifierBubble}
                            style={{
                                position: 'absolute',
                                width: 'initial',
                                top: 'calc(100% + 20px)',
                                right: 'calc(100% - 1000px)',
                            }}
                        >
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '130px 250px 100px 80px 80px 80px',
                                    fontWeight: 500,
                                    alignItems: 'end',
                                }}
                            >
                                <div>Transaction Date</div>
                                <div>Customer Name ({`${priceStatistics.CustomerType}`})</div>
                                <div style={{ display: 'flex', justifyContent: 'end' }}>
                                    Unit Rebate
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'end' }}>Sell</div>
                                <div style={{ display: 'flex', justifyContent: 'end' }}>
                                    Profit %
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'end' }}>
                                    Discount
                                </div>
                            </div>
                            <div
                                className='a-divider'
                                style={{ marginTop: 10, marginBottom: 10 }}
                            />
                            <Scrollbars
                                className={'scroll-bar'}
                                renderTrackHorizontal={() => <div />}
                                autoHeight
                                autoHeightMin={0}
                                autoHeightMax={210}
                                // height={210}
                            >
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '130px 250px 100px 80px 80px 80px',
                                        rowGap: 5,
                                    }}
                                >
                                    {priceStatistics.SalesHistory.map((row) => (
                                        <>
                                            <div
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {new Date(row.TransactionDate).toLocaleDateString(
                                                    'en-US',
                                                    {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        year: 'numeric',
                                                    }
                                                )}
                                            </div>
                                            <div>{row.CustomerName}</div>
                                            <div style={{ display: 'flex', justifyContent: 'end' }}>
                                                {`$${formatMoney(row.UnitRebate)}`}
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'end' }}>
                                                {`$${formatMoney(row.UnitPrice)}`}
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'end' }}>
                                                {row.GrossProfit === null
                                                    ? 'N/A'
                                                    : `${row.GrossProfit}%`}
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'end' }}>
                                                {row.Discount}%
                                            </div>
                                        </>
                                    ))}
                                </div>
                            </Scrollbars>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

// TODO typescriptify away this generic
interface RebatePopupParams {
    queryInfo: QueryInfo<FetchRebateAnalyticsReturnPayload>
    rebateStatus: FetchRebateStatusReturnPayload
}
const RebateAnalyticsPopup = (props: RebatePopupParams) => {
    const { queryInfo, rebateStatus } = props
    const [showingMore, setShowingMore] = useState(false)

    const [rebateHeaderCustomerView, setRebateHeaderCustomerView] =
        useState<RebateHeaderCustView[]>()

    useEffect(() => {
        if (rebateStatus.SimilarOnRebate) {
            const arrayForSort = [...rebateStatus.RebateHeaderCustomerView!]
            const tempView = arrayForSort.sort((a, b) => {
                if (a.MfgRebateCode < b.MfgRebateCode) {
                    return -1
                }
                if (a.MfgRebateCode > b.MfgRebateCode) {
                    return 1
                }
                return 0
            })
            setRebateHeaderCustomerView(tempView)
        }
    }, [rebateStatus])

    return (
        <div
            className={
                rebateStatus.SimilarOnRebate ? styles.similarRebateBubble : styles.identifierBubble
            }
            style={{ fontSize: 14, fontWeight: 400 }}
        >
            {queryInfo.loading !== LOADING_STATES.DONE ? (
                <div className={'k-i-loading'} style={{ fontSize: 50, height: '10vh' }} />
            ) : queryInfo.data ? (
                <div style={{ position: 'relative' }}>
                    <div>
                        {rebateStatus.SimilarOnRebate ? (
                            <>
                                <div>
                                    Similar products on rebate. Request to add part number to
                                    existing rebate.
                                </div>

                                <br />

                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '135px 100px 100px',
                                        fontWeight: 500,
                                    }}
                                >
                                    <div>Rebate Number</div>
                                    <div>Effective Date</div>
                                    <div>Expiration Date</div>
                                </div>
                                <div
                                    className='a-divider'
                                    style={{ marginTop: 10, marginBottom: 10 }}
                                />
                                <Scrollbars
                                    className={'scroll-bar'}
                                    renderTrackHorizontal={() => <div />}
                                    autoHeight
                                    autoHeightMin={0}
                                    autoHeightMax={200}
                                    // height={210}
                                >
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '130px 100px 100px',
                                            rowGap: 5,
                                        }}
                                    >
                                        {rebateHeaderCustomerView &&
                                            rebateHeaderCustomerView.map((row) => (
                                                <>
                                                    <div
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <AWCButton
                                                            type={BUTTON_TYPES.LINK}
                                                            onClick={() =>
                                                                window.open(
                                                                    `${getBaseURL()}masterclient/main.aspx?OpenDialog=Rebate&RebateKey=${
                                                                        row.RebateKey
                                                                    }`
                                                                )
                                                            }
                                                        >
                                                            {row.MfgRebateCode}
                                                        </AWCButton>
                                                    </div>
                                                    <div style={{ textAlign: 'center' }}>
                                                        {new Date(
                                                            row.EffectiveDate
                                                        ).toLocaleDateString('en-US', {
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            year: 'numeric',
                                                        })}
                                                    </div>
                                                    <div style={{ textAlign: 'center' }}>
                                                        {new Date(
                                                            row.ExpirationDate
                                                        ).toLocaleDateString('en-US', {
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            year: 'numeric',
                                                        })}
                                                    </div>
                                                </>
                                            ))}
                                    </div>
                                </Scrollbars>
                                <div style={{ fontStyle: 'italic', fontSize: 12, marginTop: 5 }}>
                                    Link will open .Net in a new tab. Select Fast Quote tab to
                                    return to Fast Quote.
                                </div>
                            </>
                        ) : queryInfo.data!.QuantityOfRebates >= 1 ? (
                            <>
                                <div style={{ marginTop: 5 }}>
                                    Other customers have rebates for this part number.
                                </div>
                                <div>Please request a rebate.</div>
                                <div
                                    className='a-divider'
                                    style={{ marginTop: 10, marginBottom: 10 }}
                                />

                                <div style={{ lineHeight: '1.2rem' }}>
                                    <div>
                                        {' '}
                                        Quantity of Rebates: {queryInfo.data!.QuantityOfRebates}
                                    </div>
                                    <div>
                                        {' '}
                                        Max:{' '}
                                        {queryInfo.data!.RebateMax.toLocaleString('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                        })}
                                    </div>
                                    <div>
                                        {' '}
                                        Min:{' '}
                                        {queryInfo.data!.RebateMin.toLocaleString('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                        })}
                                    </div>
                                    <div>
                                        {' '}
                                        Avg:{' '}
                                        {queryInfo.data!.RebateAvg.toLocaleString('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                        })}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginTop: 10,
                                        width: '100%',
                                    }}
                                >
                                    {showingMore ? (
                                        <AWCButton
                                            type={BUTTON_TYPES.LINK}
                                            onClick={() => setShowingMore(false)}
                                        >
                                            Hide
                                        </AWCButton>
                                    ) : (
                                        <AWCButton
                                            type={BUTTON_TYPES.LINK}
                                            onClick={() => setShowingMore(true)}
                                        >
                                            Show All
                                        </AWCButton>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div>No rebate exists.</div>
                        )}

                        {showingMore && (
                            <div
                                className={styles.identifierBubble}
                                style={{
                                    position: 'absolute',
                                    width: 'initial',
                                    top: 'calc(100% + 20px)',
                                    right: 'calc(100% - 200px)',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '200px 200px 80px 80px 100px 80px',
                                        fontWeight: 400,
                                    }}
                                >
                                    <div>Customer</div>
                                    <div>Customer Type</div>
                                    <div>Rebate #</div>
                                    <div>Rebate $</div>
                                    <div>Rebated Cost</div>
                                    <div>% List</div>
                                </div>
                                <div
                                    className='a-divider'
                                    style={{ marginTop: 10, marginBottom: 10 }}
                                />
                                <Scrollbars
                                    className={'scroll-bar'}
                                    renderTrackHorizontal={() => <div />}
                                    autoHeight
                                    autoHeightMin={0}
                                    autoHeightMax={200}
                                    // height={210}
                                >
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '200px 200px 80px 80px 100px 80px',
                                            rowGap: 5,
                                        }}
                                    >
                                        {queryInfo.data?.RebateAnalyticRows.map((row) => (
                                            <>
                                                <div
                                                    style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {row.CustomerName}
                                                </div>
                                                <div>{row.CustomerType}</div>
                                                <div>{row.MfgRebateCode}</div>
                                                <div>
                                                    {row.RebateAmount.toLocaleString('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    })}
                                                </div>
                                                <div>
                                                    {row.RebateCost.toLocaleString('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    })}
                                                </div>
                                                <div>{row.RebateListPercentage}</div>
                                            </>
                                        ))}
                                    </div>
                                </Scrollbars>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div>No rebate exists.</div>
            )}
        </div>
    )
}

export const SpannifyText = (props: any) => {
    let { text, font, textRows }: { text: string; font: string; textRows: number } = props
    if (!font) {
        font = '12px roboto'
    }
    const maxRows = textRows
    const getTextWidth = (text: string, font: string) => {
        // re-use canvas object for better performance
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')!
        context.font = font
        const metrics = context.measureText(text)
        return metrics.width
    }
    const [ref, bounds] = useMeasure()
    const getTextArray = (text: string, font: string) => {
        let words = text.split(' ')
        let lines: string[] = []
        let currentLine: string[] = []
        for (let i = 0; i < words.length; i++) {
            let nextWord = words[i]
            let newLine = [...currentLine, nextWord].join(' ')
            let newWidth = getTextWidth(newLine, font)
            if (newWidth > bounds.width) {
                // if there are previous words put them in a line
                if (currentLine.length > 0) {
                    lines.push(currentLine.join(' '))
                    currentLine = [nextWord]
                } else {
                    lines.push(nextWord)
                }
            } else {
                currentLine.push(nextWord)
            }
        }
        if (currentLine.length > 0) {
            lines.push(currentLine.join(' '))
        }
        return lines
    }
    let rows = text ? getTextArray(text, font) : [] //getTextWidth(text, '12pt roboto')
    let finalArray
    //4 lines that fit and 1 that doesn't
    if (rows.length > maxRows)
        finalArray = [...rows.slice(0, maxRows - 1), rows.slice(maxRows - 1).join(' ')]
    else finalArray = rows
    return (
        <div ref={ref}>
            {finalArray.slice(0, maxRows).map((row, i) =>
                i === finalArray.length - 1 ? (
                    <div
                        key={`${row}${i}`}
                        style={{
                            maxWidth: bounds.width - 60,
                            overflow: 'hidden',
                            display: 'inline-block',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            float: 'left',
                        }}
                    >
                        {row}
                    </div>
                ) : (
                    <div key={`${row}${i}`}>{row}</div>
                )
            )}
            {props.children}
        </div>
    )
}

interface MetaInfoProps {
    dataItem: PayloadOrderDetailViewRow
    query: QueryInfo<FetchRebateAnalyticsReturnPayload>
    customerType?: number
}

const CondensedMetaInfoColumn = (props: MetaInfoProps) => {
    const { dataItem, query, customerType } = props

    const isLargeView = useMediaQuery({
        query: '(min-width: 1484px)',
    })
    const currentAccount = useAppSelector((state) => state.order.currentAccount!)

    let cost = dataItem.Cost || 0
    let rebate = dataItem.Rebate || 0
    let price = dataItem.UnitPrice || 0
    let margin = price === 0 ? 0 : (price - (cost - rebate)) / price
    let marginPercentage = Math.round(margin * 100)
    let metaInfo: FetchPriceReturnPayload | undefined = dataItem.metaInfo?.priceInfo

    return (
        <>
            <div
                style={{
                    gridRow: '1 / 3',
                    gridColumn: '10 / 11',
                    backgroundColor: '#f0f0f0',

                    // borderLeft: 'solid 3px var(--border1)',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    padding: 0,
                    paddingLeft: 10,
                    paddingTop: isLargeView ? 18 : 24,
                    fontSize: 14,
                }}
            >
                <div
                    style={{
                        fontWeight: 300,
                        height: 21,
                        margin: '0px auto 10px',
                        justifySelf: 'center',
                    }}
                >
                    ${formatMoney(dataItem.Cost)}{' '}
                    <div style={{ display: 'inline-block' }}>
                        {metaInfo && (
                            <HoverIcon
                                growLeft
                                renderIcon={() => <span className={'a-i-info'}></span>}
                                style={{ verticalAlign: 'middle', marginLeft: 5 }}
                            >
                                {(closeBubble: () => void) =>
                                    metaInfo && (
                                        <div
                                            className={styles.identifierBubble}
                                            style={{ width: 'initial' }}
                                        >
                                            <div className={styles.costPopupGrid}>
                                                <div>Standard Cost:</div>{' '}
                                                <div>$ {formatMoney(metaInfo.Cost)}</div>
                                                <div>Last Updated:</div>{' '}
                                                <div>
                                                    {new Date(
                                                        metaInfo.PricingEffectiveDate
                                                    ).toLocaleDateString()}
                                                </div>
                                                <div>Rebated Cost:</div>{' '}
                                                <div>$ {formatMoney(metaInfo.RebatedCost)}</div>
                                                <div>MM Contact:</div>{' '}
                                                <div>{metaInfo.MaterialsManager || 'None'}</div>
                                            </div>
                                        </div>
                                    )
                                }
                            </HoverIcon>
                        )}
                    </div>
                </div>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content max-content',
                        justifyContent: 'center',
                        width: '100%',
                        fontWeight: 300,
                        rowGap: 4,
                    }}
                >
                    {/* margin */}
                    <div
                        style={{
                            fontWeight: 300,
                            justifySelf: 'flex-end',
                            marginRight: 6,
                        }}
                    >
                        Margin:
                    </div>
                    <div
                        style={{ fontWeight: 600, fontSize: 14, color: '#00be07', margin: 'auto' }}
                    >
                        {marginPercentage}%
                    </div>
                    {/* rebate */}
                    <div
                        style={{
                            fontWeight: 300,
                            justifySelf: 'flex-end',
                            marginRight: 6,
                        }}
                    >
                        Rebate:
                    </div>
                    <div>
                        {dataItem.metaInfo?.rebateStatus?.RebateStatus && (
                            <HoverIcon
                                growLeft={true}
                                growDown={true}
                                renderIcon={() => {
                                    let rebateStatus = dataItem.metaInfo!.rebateStatus!.RebateStatus
                                    if (
                                        currentAccount &&
                                        currentAccount
                                            .CustomerTypeName!.toLowerCase()
                                            .match(/(reseller)|(exporter)|(uncertain)/)
                                    ) {
                                        rebateStatus = 'N/A'
                                    }

                                    if (
                                        !dataItem.metaInfo?.rebateStatus?.SimilarOnRebate &&
                                        query.data?.QuantityOfRebates === 0
                                    ) {
                                        rebateStatus = 'N/A'
                                    }

                                    return rebateStatus === 'N/A' ? (
                                        <div style={{ fontWeight: 300, justifySelf: 'center' }}>
                                            N/A
                                        </div>
                                    ) : (
                                        <span className={getRebateClassName(rebateStatus)}></span>
                                    )
                                }}
                            >
                                {(closeBubble: () => void) => {
                                    let rebateStatus = dataItem.metaInfo!.rebateStatus!
                                    let info: FetchRebateStatusReturnPayload = rebateStatus

                                    let expirationDate = new Date(
                                        info.ExpirationDate
                                    ).toLocaleDateString()
                                    let effectiveDate = new Date(
                                        info.EffectiveDate
                                    ).toLocaleDateString()
                                    if (info.RebateStatus === 'YELLOW')
                                        return (
                                            <RebateAnalyticsPopup
                                                queryInfo={query}
                                                rebateStatus={info}
                                            />
                                        )
                                    if (
                                        info.RebateStatus === 'RED' ||
                                        info.RebateStatus === 'GREEN'
                                    ) {
                                        return (
                                            <div
                                                className={styles.rebateBubble}
                                                style={{ fontWeight: 400 }}
                                            >
                                                <div>
                                                    <span style={{ fontWeight: 500 }}>
                                                        Rebate #:{' '}
                                                    </span>
                                                    <AWCButton
                                                        type={BUTTON_TYPES.LINK}
                                                        onClick={() =>
                                                            window.open(
                                                                `${getBaseURL()}masterclient/main.aspx?OpenDialog=Rebate&RebateKey=${
                                                                    info.RebateKey
                                                                }`
                                                            )
                                                        }
                                                    >
                                                        {info.MfgRebateCode}
                                                    </AWCButton>
                                                </div>
                                                <div style={{ display: 'flex' }}>
                                                    <div style={{ width: 90 }}></div>
                                                    <div
                                                        style={{
                                                            fontStyle: 'italic',
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        Link will open .Net in a new tab. Select
                                                        Fast Quote tab to return to Fast Quote.
                                                    </div>
                                                </div>
                                                <div>
                                                    <span style={{ fontWeight: 500 }}>Type: </span>{' '}
                                                    {info.RebateTypeDescription}
                                                </div>

                                                <div>
                                                    <span style={{ fontWeight: 500 }}>
                                                        Effective Date:{' '}
                                                    </span>{' '}
                                                    {effectiveDate}
                                                </div>
                                                <div>
                                                    <span style={{ fontWeight: 500 }}>
                                                        Expiration Date:{' '}
                                                    </span>
                                                    {expirationDate}
                                                </div>
                                            </div>
                                        )
                                    } else {
                                        return (
                                            <div
                                                className={styles.identifierBubble}
                                                // TODO fix?
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                    width: 'initial',
                                                }}
                                            >
                                                <div>No rebate exists</div>
                                            </div>
                                        )
                                    }
                                }}
                            </HoverIcon>
                        )}
                    </div>

                    {/* CPA */}
                    <div
                        style={{
                            fontWeight: 300,
                            justifySelf: 'flex-end',
                            marginRight: 6,
                        }}
                    >
                        CPA:
                    </div>
                    {dataItem.metaInfo?.priceInfo?.CustomerContractNumber ? (
                        <div>
                            <HoverIcon
                                growDown
                                growLeft
                                renderIcon={
                                    // Uncertain:
                                    // 16
                                    // Icon: R

                                    // Domestic Reseller, Export Reseller, Integrated Supplier:
                                    // 11, 12, 14
                                    // Icon: R

                                    // Original Equipment Manufacturer (OEM), System Integrator/Fabricator, Engineering Contractor, Installation Contractor:
                                    // 8, 9, 13, 15
                                    // Icon: EB

                                    // End User
                                    // 10
                                    // Icon: EU

                                    dataItem.metaInfo?.priceInfo?.CustomerContractType !==
                                    'Customer Type'
                                        ? () => <span className='a-i-checkmark' />
                                        : () => {
                                              switch (customerType) {
                                                  case 16:
                                                  case 11:
                                                  case 12:
                                                  case 14:
                                                      return (
                                                          <span className={styles.circle}>R</span>
                                                      )
                                                  case 8:
                                                  case 9:
                                                  case 13:
                                                  case 15:
                                                      return (
                                                          <span className={styles.circle}>EB</span>
                                                      )
                                                  case 10:
                                                      return (
                                                          <span className={styles.circle}>EU</span>
                                                      )
                                                  default:
                                                      return (
                                                          <span className={styles.circle}>N/A</span>
                                                      )
                                              }
                                          }
                                }
                            >
                                {(closeBubble: () => void) => {
                                    let priceInfo = dataItem.metaInfo!.priceInfo!
                                    return (
                                        <div
                                            className={styles.identifierBubble}
                                            style={{ width: 'max-content' }}
                                        >
                                            {dataItem.metaInfo?.priceInfo?.CustomerContractType !==
                                            'Customer Type' ? (
                                                <div className={styles.cpaGrid}>
                                                    <>
                                                        <div>CPA#:</div>
                                                        <div>
                                                            {priceInfo.CustomerContractNumber}
                                                        </div>
                                                    </>
                                                    <>
                                                        <div>Type:</div>
                                                        <div>{priceInfo.CustomerContractType}</div>
                                                    </>
                                                    <>
                                                        <div>Basis:</div>
                                                        <div>{priceInfo.CustomerContractBasis}</div>
                                                    </>
                                                    <>
                                                        <div>Renewal:</div>
                                                        <div>
                                                            {new Date(
                                                                priceInfo.CustomerContractExpiration
                                                            ).toLocaleDateString('en-US', {
                                                                month: '2-digit',
                                                                day: '2-digit',
                                                                year: 'numeric',
                                                            })}
                                                        </div>
                                                    </>
                                                </div>
                                            ) : customerType ? (
                                                (() => {
                                                    switch (customerType) {
                                                        case 16:
                                                            return (
                                                                <div style={{ width: 220 }}>
                                                                    Reseller default CPA in use as
                                                                    Customer Type is uncertain.
                                                                    Update customer type to improve
                                                                    pricing accuracy.
                                                                </div>
                                                            )
                                                        case 11:
                                                        case 12:
                                                        case 14:
                                                            return (
                                                                <div style={{ width: 220 }}>
                                                                    Reseller default CPA in use. If
                                                                    the customer type is incorrect,
                                                                    please update customer type to
                                                                    improve pricing accuracy.
                                                                </div>
                                                            )
                                                        case 8:
                                                        case 9:
                                                        case 13:
                                                        case 15:
                                                            return (
                                                                <div style={{ width: 220 }}>
                                                                    Equipment Builder default CPA in
                                                                    use. If the customer type is
                                                                    incorrect, please update
                                                                    customer type to improve pricing
                                                                    accuracy.
                                                                </div>
                                                            )
                                                        case 10:
                                                            return (
                                                                <div style={{ width: 220 }}>
                                                                    End User default CPA in use. If
                                                                    the customer type is incorrect,
                                                                    please update customer type to
                                                                    improve pricing accuracy.
                                                                </div>
                                                            )
                                                        default:
                                                            return <div>N/A</div>
                                                    }
                                                })()
                                            ) : (
                                                <div></div>
                                            )}
                                        </div>
                                    )
                                }}
                            </HoverIcon>
                        </div>
                    ) : (
                        <div style={{ fontWeight: 300, justifySelf: 'flex-end', marginRight: 6 }}>
                            N/A
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

interface AvailabilityInputProps {
    value: Date | null
    initialValue: Date | null
    needDate?: Date
    mode: 'A' | 'B' | 'C' | 'D'
    rowId: string
    onChange<T extends 'A' | 'B' | 'C' | 'D'>(
        mode: T,
        value: T extends 'D' ? number | null : Date | null
    ): void
}

const getTextWidth = (text: string, font: string) => {
    // re-use canvas object for better performance
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')!
    context.font = font
    const metrics = context.measureText(text)
    return metrics.width
}

const getAvailability = (
    estimatedDelivery: EstimatedDeliveryReturnPayload | null,
    noNotes: boolean,
    dataItem: PayloadOrderDetailViewRow,
    needDate: Date | null,
    estimatedDate: Date | null,
    availabilityMode: string,
    isPreview: boolean
) => {
    if (!estimatedDelivery) return <div></div>

    let dateValue = getAvailabilityDate(dataItem, isPreview, estimatedDelivery)

    if (
        noNotes ||
        dataItem.ExpectedOnDockDate ||
        availabilityMode === 'A' ||
        availabilityMode === 'B'
    )
        return (
            <div
                style={
                    needDate && estimatedDate && estimatedDate > needDate ? { color: 'red' } : {}
                }
            >
                {getAvailabilityString(dataItem, availabilityMode, dateValue, noNotes)}
            </div>
        )

    return (
        <>
            <div
                style={
                    needDate && estimatedDate && estimatedDate > needDate ? { color: 'red' } : {}
                }
            >
                {getAvailabilityString(
                    dataItem,
                    availabilityMode,
                    new Date(estimatedDelivery.AvailableEstimatedDeliveryDate),
                    noNotes
                )}{' '}
                (qty. {estimatedDelivery.TotalAvailable})
            </div>
            <div
                style={
                    needDate && estimatedDate && estimatedDate > needDate ? { color: 'red' } : {}
                }
            >
                {getAvailabilityString(
                    dataItem,
                    availabilityMode,
                    getAvailabilityDate(dataItem, isPreview, estimatedDelivery),
                    noNotes
                )}{' '}
                (qty.
                {dataItem.OrderQuantity! - estimatedDelivery.TotalAvailable})
            </div>
        </>
    )
}

const getAvailabilityDate = (
    dataItem: PayloadOrderDetailViewRow,
    isPreview: boolean,
    estimatedDelivery: EstimatedDeliveryReturnPayload
): Date => {
    if (dataItem.BalanceShipDate) return new Date(dataItem.BalanceShipDate)

    if (isPreview) {
        if (dataItem.NeedDate) return new Date(dataItem.NeedDate)
    } else {
        if (dataItem.ExpectedOnDockDate) return new Date(dataItem.ExpectedOnDockDate)
    }

    return new Date(estimatedDelivery!.EstimatedDeliveryDate!)
}

const getAvailabilityString = (
    dataItem: PayloadOrderDetailViewRow,
    mode: string,
    value: Date,
    noNotes: boolean
): string => {
    // if in search quote modal
    if (noNotes) {
        return value.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        })
    }

    if (mode === 'A') {
        return getAROAvailabilityString(dataItem)
    } else if (mode === 'B') {
        return getBalanceShipDateString(dataItem)
    } else if (mode === 'D') {
        let numWeeks = getBusinessWeeksCount(new Date(), value)
        return numWeeks === 1 ? `${numWeeks} Week` : `${numWeeks} Weeks`
    } else {
        return value.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        })
    }
}

const getAROAvailabilityString = (dataItem: PayloadOrderDetailViewRow): string => {
    if (dataItem.QuotedDeliveryUOM === 'C') return dataItem.QuotedDeliveryOverride
    else if (dataItem.QuotedDeliveryUOM === 'W')
        return `${dataItem.QuotedDelivery} Week${dataItem.QuotedDelivery === 1 ? '' : 's'}`
    else return `${dataItem.QuotedDelivery} Day${dataItem.QuotedDelivery === 1 ? '' : 's'}`
}

const getBalanceShipDateString = (dataItem: PayloadOrderDetailViewRow): string => {
    if (dataItem.QuotedExpeditedDeliveryDate)
        return new Date(dataItem.QuotedExpeditedDeliveryDate).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        })
    else if (dataItem.BalanceShipDate) {
        const balanceShipDate = new Date(dataItem.BalanceShipDate)
        if (
            balanceShipDate.getFullYear() < 3000 &&
            dataItem.PartNumber?.trim().toUpperCase() !== 'MI NOT ON FILE'
        )
            return balanceShipDate.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
            })
    }
    return 'TBD'
}

const isSameDate = (date1: Date, date2: Date) => {
    return date1.toLocaleDateString() === date2.toLocaleDateString()
}

const AvailabilityInput = (props: AvailabilityInputProps) => {
    const {
        mode,
        //onChange,
        rowId,
        value,
        initialValue,
        needDate,
    } = props
    const overrided = value && initialValue && !isSameDate(value, initialValue)
    const estimatedDate = value || initialValue
    const dispatch = useAppDispatch()
    const weeks = estimatedDate && getBusinessWeeksCount(new Date(), estimatedDate)

    const isLargeView = useMediaQuery({
        query: '(min-width: 1484px)',
    })
    let font = '14px roboto'

    let weeksLength = getTextWidth((weeks || 0).toString(), font)

    return (
        <div>
            {mode === 'C' ? (
                <div>
                    <div
                        className={
                            needDate && estimatedDate && estimatedDate > needDate ? 'a-invalid' : ''
                        }
                        style={{
                            whiteSpace: 'nowrap',
                            position: 'relative',
                            display: 'flex',
                        }}
                    >
                        {overrided && (
                            <HoverIcon
                                growDown={true}
                                renderIcon={() => (
                                    <div
                                        style={{
                                            fontWeight: 300,
                                            fontSize: 14,
                                            position: 'relative',
                                            paddingTop: 12,
                                            alignSelf: 'flex-end',
                                            color:
                                                needDate &&
                                                estimatedDate &&
                                                estimatedDate > needDate
                                                    ? 'red'
                                                    : '',
                                        }}
                                    >
                                        *&nbsp;
                                    </div>
                                )}
                            >
                                {/* TODO figure out why typescript can't tell not null withoutn the && */}
                                {(closeBubble: () => void) => (
                                    <div
                                        className={styles.userOverrideBubble}
                                        onClick={(e: any) => e.stopPropagation()}
                                    >
                                        <div>
                                            <div
                                                style={{
                                                    fontWeight: 400,
                                                    paddingBottom: 5,
                                                }}
                                            >
                                                User Override
                                            </div>
                                            <div style={{ fontWeight: 300 }}>Original System</div>
                                            <div
                                                style={{
                                                    fontWeight: 300,
                                                }}
                                            >
                                                <span>
                                                    Date:&nbsp;
                                                    <span style={{ fontWeight: 400 }}>
                                                        {initialValue?.toLocaleDateString('en-US', {
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            year: '2-digit',
                                                        })}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </HoverIcon>
                        )}
                        <DatePicker
                            minDate={new Date()}
                            onChange={(date) => {
                                props.onChange('C', date)
                            }}
                            validityText={
                                needDate && estimatedDate && estimatedDate > needDate
                                    ? 'After need date'
                                    : ''
                            }
                            value={estimatedDate}
                            onValidityChange={(valid: any) =>
                                valid
                                    ? dispatch(
                                          markRowValid({
                                              rowId: rowId,
                                              field: 'ExpectedOnDockDate',
                                          })
                                      )
                                    : dispatch(
                                          markRowInvalid({
                                              rowId: rowId,
                                              field: 'ExpectedOnDockDate',
                                          })
                                      )
                            }
                            style={{
                                backgroundColor: 'initial',
                            }}
                        />
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        position: 'relative',
                        fontSize: 14,
                        width: '100%',
                        height: 32,
                        display: 'flex',
                        marginTop: 10,
                    }}
                >
                    {overrided && (
                        <HoverIcon
                            growDown={true}
                            renderIcon={() => (
                                <span
                                    style={{
                                        fontWeight: 300,
                                        fontSize: 14,
                                        position: 'relative',
                                        alignSelf: 'flex-end',
                                        color:
                                            needDate && estimatedDate && estimatedDate > needDate
                                                ? 'red'
                                                : '',
                                        top: 2,
                                    }}
                                >
                                    *&nbsp;
                                </span>
                            )}
                        >
                            {/* TODO figure out why typescript can't tell not null withoutn the && */}
                            {(closeBubble: () => void) => (
                                <div
                                    className={styles.userOverrideBubble}
                                    onClick={(e: any) => e.stopPropagation()}
                                    style={{ color: 'black' }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 400,
                                                paddingBottom: 5,
                                            }}
                                        >
                                            User Override
                                        </div>
                                        <div style={{ fontWeight: 300 }}>Original System</div>
                                        <div
                                            style={{
                                                fontWeight: 300,
                                            }}
                                        >
                                            <span>
                                                Date:&nbsp;
                                                <span style={{ fontWeight: 400 }}>
                                                    {initialValue?.toLocaleDateString('en-US', {
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        year: '2-digit',
                                                    })}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </HoverIcon>
                    )}
                    <div
                        style={{
                            position: 'relative',
                            borderBottom:
                                needDate && estimatedDate && estimatedDate > needDate
                                    ? '2px solid red'
                                    : '1px solid #0062FF',
                            paddingBottom: 8,
                            color:
                                needDate && estimatedDate && estimatedDate > needDate ? 'red' : '',
                            width: overrided ? '88%' : '95%',
                            minWidth: overrided ? 118 : 127,
                        }}
                        onFocus={(event: any) => {
                            event.target.select()
                        }}
                    >
                        <input
                            style={{
                                fontWeight: 300,
                                border: 'none',
                                outline: 'none',
                                height: 18,
                                fontSize: 14,
                                color:
                                    needDate && estimatedDate && estimatedDate > needDate
                                        ? 'red'
                                        : '',
                                background: 'none',
                                width: weeksLength,
                            }}
                            onFocus={(event: any) => {
                                event.target.select()
                            }}
                            value={weeks || ''}
                            type='text'
                            pattern='^$|[0-9]{0,3}'
                            onChange={(e: any) => {
                                const value = parseInt(e.target.value)
                                props.onChange('D', value)
                            }}
                        />
                        &nbsp;
                        <span style={{ height: 18 }}>
                            {!weeks || weeks === 1 ? 'Week' : 'Weeks'}
                        </span>
                    </div>
                    {needDate && estimatedDate && estimatedDate > needDate && (
                        <div
                            style={{
                                marginTop: -2,
                                height: 18,
                                color: '#f44336',
                                fontWeight: 400,
                                position: 'absolute',
                                bottom: isLargeView ? -20 : -20,
                                width: '100%',
                                fontSize: 10.5, // Should always be 10.5 to match MUI helper text
                                left: overrided ? 9 : 0,
                                letterSpacing: '0.03333em',
                            }}
                        >
                            After need date
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
interface PartNumberChangeInfo {
    PartSearchInfo: PartSearchInfo | null
    PartInfo: FetchPartInfoReturnPayload
    PartXRefInfo: string
}
const SortableItemUI = (props: any) => {
    const { isDisabled, style, attributes, forwardRef, reviewingQuoteOutput, isPreview } = props
    const orderHeader: PayloadOrderHeaderViewRow = props.dataItem.orderHeader
    let dataItem: PayloadOrderDetailViewRow = props.dataItem
    let { focused, last, lineRefs, accountCode, noNotes } = props.dataItem
    // let dataItem: OrderDetailViewRow & { rowId: string } = props.dataItem
    const dispatch = useAppDispatch()
    let tempRow = !!dataItem.temp
    const [editingDescription, setEditingDescription] = useState(false)

    const currentAccount = useAppSelector((state) => state.order.currentAccount!)
    const currentShipTo = useAppSelector((state) => state.order.currentShipTo)
    const toContact = useAppSelector((state) => state.order.toContact!)
    const orderFilenames = useAppSelector((state) => state.order.orderFilenames)
    const currentCustomer = useAppSelector((state) => state.order.currentCustomer)!
    const showImages = useAppSelector((state) => state.order.showImages)
    const customerMode = useAppSelector((state) => state.meta.customerMode)
    const previewMode = useAppSelector((state) => state.meta.previewMode)
    const availabilityMode = useAppSelector((state) => state.order.orderHeader[0].AvailabilityMode)
    const getOrderMiscInfoLoading = useAppSelector((state) => state.order.getOrderMiscInfoLoading)

    let invalidRows = useAppSelector((state) => state.order.invalidLines)

    const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

    const [tempDescription, setTempDescription] = useState(dataItem.Description || '')
    // const classNames = ['col-xs-6 col-sm-3']
    const classNames = []
    if (isDisabled) {
        classNames.push('k-state-disabled')
    }
    const [lineItemNotesModalOpen, setLineItemNotesModalOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState(false)

    const onPartNumberChange = async (changeInfo: PartNumberChangeInfo | null) => {
        if (!changeInfo) {
            // clearLineItem
            dispatch(markRowInvalid({ rowId: dataItem.rowId, field: 'partNumber' }))
            return
        }

        if (changeInfo) setLoading(true)
        // updateLine({
        //     PartKey: changeInfo.PartInfo.Partkey,
        //     LineItemNumber: dataItem.LineItemNumber,
        //     Description: changeInfo.PartInfo.Description,
        //     PartNumber: changeInfo.PartInfo.PartNumber,
        //     SymbolNumber: changeInfo.PartXRefInfo,
        //     rowId: dataItem.rowId,
        //     ExpectedOnDockDate: undefined,
        //     OriginalOnDockDate: undefined,
        //     NeedDate: undefined,
        // })
        await dispatch(
            updateLine({
                PartKey: changeInfo.PartInfo.Partkey,
                LineItemNumber: dataItem.LineItemNumber,
                Description: changeInfo.PartInfo.Description,
                PartNumber: changeInfo.PartInfo.PartNumber,
                SymbolNumber: changeInfo.PartXRefInfo,
                rowId: dataItem.rowId,
                ExpectedOnDockDate: undefined,
                OriginalOnDockDate: undefined,
                NeedDate: undefined,
            })
        )
        setLoading(false)
    }
    const isCurrentPart = !loading && !tempRow && !invalidRows[dataItem.rowId]?.partNumber

    const [partExists, setPartExists] = useState<boolean>(false)

    useEffect(() => {
        if (getOrderMiscInfoLoading === LOADING_STATES.DONE && isCurrentPart) {
            setPartExists(true)
        }
    }, [getOrderMiscInfoLoading])

    function addWorkDays(startDate: Date, days: number) {
        if (isNaN(days)) {
            return
        }
        if (!(startDate instanceof Date)) {
            return
        }
        // Get the day of the week as a number (0 = Sunday, 1 = Monday, .... 6 = Saturday)
        var dow = startDate.getDay()
        var daysToAdd = days
        // If the current day is Sunday add one day
        if (dow === 0) daysToAdd++
        // If the start date plus the additional days falls on or after the closest Saturday calculate weekends
        if (dow + daysToAdd >= 6) {
            //Subtract days in current working week from work days
            var remainingWorkDays = daysToAdd - (5 - dow)
            //Add current working week's weekend
            daysToAdd += 2
            if (remainingWorkDays > 5) {
                //Add two days for each working week by calculating how many weeks are included
                daysToAdd += 2 * Math.floor(remainingWorkDays / 5)
                //Exclude final weekend if remainingWorkDays resolves to an exact number of weeks
                if (remainingWorkDays % 5 === 0) daysToAdd -= 2
            }
        }
        startDate.setDate(startDate.getDate() + daysToAdd)
        return startDate
    }

    //place to keep state for if it isn't on the dataitem i.e. when we load for preview
    const [availabilityInfo, setAvailabilityInfo] = useState<
        undefined | PartsAvailabilityReturnPayload
    >()
    const [partInfoState, setPartInfo] = useState<undefined | FetchPartInfoReturnPayload>()
    useEffect(() => {
        let dataItem: PayloadOrderDetailViewRow = props.dataItem
        if (dataItem.PartKey && props.reviewingQuoteOutput && !props.dataItem.metaInfo?.partInfo) {
            fetchInvtPartInfo({
                PartKey: dataItem.PartKey,
                SearchMfgPartNumberFallback: false,
            }).then((info) => setPartInfo(info || undefined))
        }
    }, [props.reviewingQuoteOutput, props.dataItem])

    useEffect(() => {
        if (customerMode || previewMode) setAvailabilityInfo(undefined)
        else {
            let dataItem: PayloadOrderDetailViewRow = props.dataItem
            if (
                dataItem.PartKey &&
                props.reviewingQuoteOutput &&
                !props.dataItem.metaInfo?.partsAvailability
            ) {
                fetchPartsAvailability({
                    Partkey: dataItem.PartKey!,
                    NeedDate: dataItem.NeedDate ? new Date(dataItem.NeedDate) : new Date(),
                    OrderNumber: dataItem.OrderNumber,
                    LineNumber: dataItem.LineItemNumber,
                    Location: orderHeader.Location!,
                    Quantity: dataItem.OrderQuantity!,
                }).then(setAvailabilityInfo)
            }
        }
    }, [props.reviewingQuoteOutput, props.dataItem, customerMode, previewMode])

    let partsAvailable = dataItem.metaInfo?.partsAvailability || availabilityInfo
    let partInfo = dataItem.metaInfo?.partInfo || partInfoState

    useEffect(() => {
        if (partInfo && !dataItem.OrderQuantity) {
            dispatch(
                updateLine({
                    rowId: dataItem.rowId,
                    OrderQuantity: partInfo.SellMinimum,
                })
            )
        }
    }, [partInfo])

    const { getRebateAnalytics, query } = useRebateAnalytics(currentAccount.CustomerTypeName)

    !reviewingQuoteOutput &&
        !currentAccount
            .CustomerTypeName!.toLowerCase()
            .match(/(reseller)|(exporter)|(uncertain)/) &&
        dataItem.PartKey &&
        dataItem.PartKey !== -1 &&
        getRebateAnalytics({
            CustomerAccountCode: accountCode,
            Partkey: dataItem.PartKey,
            Warehouse: orderHeader.AllocationWarehouse!,
        })

    // if (!isCurrentPart) {
    //     dataItem = { LineItemNumber: dataItem.LineItemNumber }
    // }

    const [showTableBubble, setShowTableBubble] = useState<boolean>(false)

    let coRootCookie = getCookie('CoRoot')
    if (coRootCookie === '') coRootCookie = 'A'

    const imageLink = `${getBaseURL()}invtforms/invtimage.ashx?partkey=${
        dataItem.PartKey
    }&co=${coRootCookie}&blank=true`

    let plck = partInfo?.ProductLifeCycleKey
    let limitedLifeCycleKeys = [3, 4, 5, 6, 7]
    let showLimitedAvailability = plck !== undefined && limitedLifeCycleKeys.includes(plck)
    let showConstrained = partInfo?.Constrained

    // useEffect(() => {
    //     dispatch(
    //         updateLine({
    //             rowId: dataItem.rowId,
    //             NeedDate: estimatedDelivery?.EstimatedDeliveryDate,
    //         })
    //     )
    // }, [estimatedDelivery])
    const estimatedDeliveryParams = makeEstimatedDeliveryParams(orderHeader, dataItem)

    const [estimatedDelivery] = useEstimatedDelivery(estimatedDeliveryParams, dataItem)

    const value = dataItem.ExpectedOnDockDate ? new Date(dataItem.ExpectedOnDockDate) : null
    const initialValue =
        estimatedDelivery && estimatedDelivery.EstimatedDeliveryDate
            ? new Date(estimatedDelivery.EstimatedDeliveryDate)
            : null

    const estimatedDate = value || initialValue
    const needDate = dataItem.OriginalOnDockDate ? new Date(dataItem.OriginalOnDockDate) : null

    const descriptionEditDone = () => {
        setEditingDescription(false)
        dispatch(
            updateLine({
                rowId: dataItem.rowId,
                Description: tempDescription,
            })
        )
    }

    return (
        <>
            {reviewingQuoteOutput && (
                <div
                    ref={(el) => {
                        forwardRef && el && forwardRef(el)
                        lineRefs.current[dataItem.LineItemNumber] = el
                    }}
                    {...attributes}
                    style={{
                        // ...getBaseItemStyle(isActive),
                        ...style,
                        position: 'relative',
                        outline: 'none',
                        // gridTemplateColumns: 'var(--handleWidth) 1fr'
                    }}
                    tabIndex={-1}
                    // className={}
                >
                    <div
                        className={clsx([
                            styles.lineItemVeil,
                            focused && styles.lineItemVeilFocused,
                        ])}
                    />

                    {/* <RowHandle /> */}
                    <div
                        // only let drag get to the handle
                        className={clsx([
                            'no-drag',
                            styles.reviewQuoteLineItemRow,
                            // noNotes && styles.reviewQuoteLineItemRowNoteless,

                            last && styles.lastLineItem,
                            classNames,
                        ])}
                    >
                        <div
                            style={{
                                fontWeight: 300,
                                fontSize: 14,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-around',
                                }}
                            >
                                <div style={{ alignSelf: 'center', display: 'contents' }}>
                                    {dataItem.LineItemNumber}
                                </div>
                                {showImages ? (
                                    <img
                                        style={{
                                            verticalAlign: 'top',
                                            maxHeight: '45px',
                                            maxWidth: '45px',
                                            width: 45,
                                        }}
                                        src={imageLink}
                                        loading='lazy'
                                        alt='Part'
                                    />
                                ) : (
                                    <div style={{ marginLeft: 45 }} />
                                )}
                            </div>
                        </div>
                        <div style={{ padding: '12px 5px', display: 'grid' }}>
                            {/* <div style={{ fontWeight: 300, fontSize: 12 }}>
                                {dataItem.metaInfo?.partInfo?.MfgName ||
                                    (partInfoState && partInfoState?.MfgName)}
                            </div> */}
                            <AWCButton
                                type={'link'}
                                style={{ fontSize: 14, fontWeight: 400, marginBottom: 7 }}
                                onClick={() => {
                                    window.open(
                                        `https://www.awc-inc.com/product/part/${dataItem.PartKey!.toString()}?VPK=${dataItem.PartKey!.toString()}`
                                    )
                                }}
                            >
                                {dataItem.PartNumber ||
                                    dataItem.metaInfo?.partInfo?.MfgPartNumber ||
                                    (partInfoState && partInfoState?.MfgPartNumber)}
                            </AWCButton>
                            {/* TODO should this have been cut */}

                            {/* {dataItem.SymbolNumber ? (
                                <div style={{ marginTop: 10, fontSize: 14, fontWeight: 400 }}>
                                    Customer Part #:{' '}
                                    <span style={{ fontSize: 14, fontWeight: 300 }}>
                                        {dataItem.SymbolNumber}
                                    </span>
                                </div>
                            ) : (
                                <div></div>
                            )} */}

                            <div
                                className={styles.lightText}
                                style={{
                                    display: 'block',
                                    overflow: 'auto',
                                    overflowWrap: 'break-word',
                                    wordWrap: 'break-word',
                                    fontWeight: 300,
                                    fontSize: 12,
                                    marginBottom: 5,
                                }}
                            >
                                {dataItem.metaInfo?.vendorName?.VendorName ? (
                                    <span style={{ fontWeight: 400 }}>
                                        {dataItem.metaInfo?.vendorName.VendorName}&nbsp;
                                    </span>
                                ) : (
                                    <></>
                                )}
                                {dataItem.Description}
                            </div>
                            {!noNotes && (
                                <>
                                    <div
                                        className={styles.lightText}
                                        style={{
                                            display: 'block',
                                            overflow: 'auto',
                                            overflowWrap: 'break-word',
                                            wordWrap: 'break-word',
                                            fontWeight: 300,
                                            fontSize: 12,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {dataItem.UnitPrice === 0 && (
                                            <span style={{ fontWeight: 500, fontSize: 14 }}>*</span>
                                        )}
                                        {dataItem.SpecialInstructions && (
                                            <>
                                                <div
                                                    style={{
                                                        fontWeight: 500,
                                                        lineHeight: '16px',
                                                        marginTop: '10px',
                                                    }}
                                                >
                                                    Line Item Notes:
                                                </div>
                                                <div>{dataItem.SpecialInstructions}</div>
                                            </>
                                        )}
                                        {previewMode && dataItem.QuoteNote && (
                                            <>
                                                <div
                                                    style={{
                                                        fontWeight: 500,
                                                        lineHeight: '16px',
                                                        marginTop: '10px',
                                                    }}
                                                >
                                                    Quote Note:
                                                </div>
                                                <div
                                                    style={{
                                                        lineHeight: '16px',
                                                    }}
                                                >
                                                    {dataItem.QuoteNote}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div
                            style={{
                                fontWeight: 300,
                                fontSize: 12,
                                textAlign: 'center',
                                display: 'block',
                                overflow: 'auto',
                                overflowWrap: 'break-word',
                                wordWrap: 'break-word',
                            }}
                        >
                            {dataItem.OrderQuantity?.toLocaleString('en-US')}
                        </div>
                        <div
                            className={styles.lightText}
                            style={{
                                display: 'flex',
                                overflow: 'auto',
                                overflowWrap: 'break-word',
                                wordWrap: 'break-word',
                                justifyContent: 'center',
                                gap: 3,
                                fontSize: 12,
                                textAlign: 'center',
                            }}
                        >
                            {dataItem.UnitPrice === 0 && <b style={{ fontSize: 14 }}>*</b>}
                            <div>
                                {dataItem.UnitPrice!.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                })}
                            </div>
                        </div>
                        {/* TODO: Figure out returnable field */}
                        <div
                            className={styles.lightText}
                            style={{ textAlign: 'center', fontSize: 12, wordBreak: 'break-all' }}
                        >
                            {(dataItem.UnitPrice! * dataItem.OrderQuantity!).toLocaleString(
                                'en-US',
                                {
                                    style: 'currency',
                                    currency: 'USD',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                }
                            )}
                        </div>
                        {/* <div className={styles.lightText} style={{ textAlign: 'center', fontSize: 12 }}>
                            {needDate ? new Date(needDate).toLocaleDateString() : 'Unspecified'}
                        </div> */}
                        <div
                            className={styles.lightText}
                            style={{ textAlign: 'center', fontSize: 12 }}
                        >
                            {getAvailability(
                                estimatedDelivery,
                                noNotes,
                                dataItem,
                                needDate,
                                estimatedDate,
                                availabilityMode,
                                isPreview
                            )}
                        </div>
                        {/* {customerMode && (
                            <div
                                style={{
                                    backgroundColor: '#e2e2e2',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    paddingLeft: 0,
                                    paddingRight: 0,
                                }}
                            >
                                {!props.dataItem.last && (
                                    <div
                                        className={styles.lineItemDelete}
                                        onClick={() => {
                                            setConfirmDelete(dataItem.rowId)
                                        }}
                                    />
                                )}
                            </div>
                        )} */}
                    </div>
                </div>
            )}
            {!reviewingQuoteOutput && (
                <div
                    ref={(el) => {
                        forwardRef && el && forwardRef(el)
                        lineRefs.current[dataItem.LineItemNumber] = el
                    }}
                    {...attributes}
                    style={{
                        // ...getBaseItemStyle(isActive),
                        ...style,
                        position: 'relative',
                        outline: 'none',
                        // gridTemplateColumns: 'var(--handleWidth) 1fr'
                    }}
                    tabIndex={-1}
                    // className={}
                >
                    <div
                        className={clsx([
                            styles.lineItemVeil,
                            focused && styles.lineItemVeilFocused,
                        ])}
                    />

                    {/* <RowHandle /> */}

                    <div
                        // only let drag get to the handle
                        className={clsx([
                            'no-drag',
                            styles.lineItemRow,
                            last && styles.lastLineItem,
                            classNames,
                        ])}
                    >
                        <div
                            style={{
                                margin: 'auto',
                                fontWeight: 300,
                                fontSize: 14,
                            }}
                        >
                            {dataItem.LineItemNumber}
                        </div>
                        <div>
                            <PartNumberField
                                onChange={onPartNumberChange}
                                partLoading={loading}
                                isCurrentPart={isCurrentPart}
                                rowId={dataItem.rowId}
                                testId={'partNumberField'}
                                accountCode={accountCode}
                                key={`partField${dataItem.rowId}`}
                                partNumber={dataItem.PartNumber || ''}
                                orderDetailRow={dataItem}
                            />
                        </div>
                        {partExists ? (
                            <>
                                <TextAreaModal
                                    title={`Line Item #${dataItem.LineItemNumber} - ${dataItem.metaInfo?.partInfo?.MfgName} ${dataItem.metaInfo?.partInfo?.MfgPartNumber}`}
                                    close={() => {
                                        setLineItemNotesModalOpen(false)
                                    }}
                                    open={lineItemNotesModalOpen}
                                    onSubmit={(newValue) => {
                                        if (!newValue && dataItem.UnitPrice === 0) {
                                            dispatch(
                                                markRowInvalid({
                                                    rowId: dataItem.rowId,
                                                    field: 'SpecialInstructions',
                                                })
                                            )
                                        } else {
                                            dispatch(
                                                markRowValid({
                                                    rowId: dataItem.rowId,
                                                    field: 'SpecialInstructions',
                                                })
                                            )
                                        }
                                        dispatch(
                                            updateLine({
                                                rowId: dataItem.rowId,
                                                SpecialInstructions: newValue,
                                            })
                                        )
                                    }}
                                    defaultValue={dataItem.SpecialInstructions}
                                    zeroPrice={dataItem.UnitPrice}
                                />
                                <div>
                                    <OnBlurFastQuoteInput
                                        value={dataItem.OrderQuantity}
                                        onChange={(value: string) =>
                                            !isNaN(parseInt(value)) &&
                                            parseInt(value) !== dataItem.OrderQuantity &&
                                            // updateLine({
                                            //     rowId: dataItem.rowId,
                                            //     OrderQuantity: parseInt(value),
                                            // })
                                            dispatch(
                                                updateLine({
                                                    rowId: dataItem.rowId,
                                                    OrderQuantity: parseInt(value),
                                                })
                                            )
                                        }
                                        validator={(value: string) => {
                                            let parsed = parseInt(value)
                                            return (
                                                /^\d+$/.test(value) &&
                                                parsed > 0 &&
                                                parsed < 1000000
                                            )
                                        }}
                                        testid='orderQuantity'
                                    />

                                    {dataItem.metaInfo!.partInfo &&
                                        dataItem.metaInfo!.partInfo.SellMinimum > 1 && (
                                            <div
                                                style={{
                                                    color: 'red',
                                                    fontSize: 12,
                                                    textAlign: 'center',
                                                    marginTop: 2.5,
                                                }}
                                            >
                                                Minimum Quantity of{' '}
                                                {dataItem.metaInfo!.partInfo!.SellMinimum}
                                            </div>
                                        )}
                                    {dataItem.OrderQuantity &&
                                        dataItem.metaInfo!.partInfo &&
                                        dataItem.OrderQuantity %
                                            dataItem.metaInfo!.partInfo.SellMultiple !==
                                            0 && (
                                            <div
                                                style={{
                                                    color: 'red',
                                                    fontSize: 12,
                                                    textAlign: 'center',
                                                    marginTop: 2.5,
                                                }}
                                            >
                                                Suggested Multiple of{' '}
                                                {dataItem.metaInfo!.partInfo.SellMultiple}
                                            </div>
                                        )}
                                </div>
                                <div
                                    style={{
                                        position: 'relative',
                                        gridRow: '1 / 3',
                                        gridColumn: '4',
                                    }}
                                >
                                    {editingDescription ? (
                                        <div>
                                            <div>
                                                <TextField
                                                    multiline={true}
                                                    rows={4}
                                                    variant={'outlined'}
                                                    style={{ width: '100%' }}
                                                    value={dataItem && tempDescription}
                                                    onChange={(e) => {
                                                        setTempDescription(
                                                            e.target.value.substr(0, 255)
                                                        )
                                                    }}
                                                    onKeyDown={(event: any) => {
                                                        if (
                                                            event.key === 'Enter' ||
                                                            event.key === 'Tab'
                                                        ) {
                                                            descriptionEditDone()
                                                        }
                                                    }}
                                                    onBlur={(event: any) => {
                                                        event.target.blur()
                                                        descriptionEditDone()
                                                    }}
                                                />
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    position: 'absolute',
                                                    bottom: 5,
                                                    width: '100%',
                                                    fontSize: 12,
                                                    fontWeight: 300,
                                                }}
                                            >
                                                <div>{tempDescription.length}/255 Characters</div>{' '}
                                                <AWCButton
                                                    type={'link'}
                                                    style={{ fontWeight: 400, fontSize: 12 }}
                                                    onClick={descriptionEditDone}
                                                >
                                                    Done
                                                </AWCButton>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 300,
                                                }}
                                            >
                                                {isCurrentPart && (
                                                    <HoverIcon
                                                        growUp={true}
                                                        renderIcon={() => (
                                                            <SpannifyText
                                                                text={dataItem.Description}
                                                                font={''}
                                                                textRows={3}
                                                            >
                                                                <SvgIcon
                                                                    icon={EditPencil}
                                                                    height={12}
                                                                    onClick={() => {
                                                                        setTempDescription(
                                                                            dataItem.Description ||
                                                                                ''
                                                                        )
                                                                        setEditingDescription(true)
                                                                    }}
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        paddingLeft: 2,
                                                                        paddingBottom: 2,
                                                                    }}
                                                                />
                                                            </SpannifyText>
                                                        )}
                                                        style={{ display: 'block' }}
                                                    >
                                                        {(closeBubble: () => void) => (
                                                            <div style={{ width: 500 }}>
                                                                <div
                                                                    className={
                                                                        styles.identifierBubble
                                                                    }
                                                                    style={{
                                                                        overflowX: 'hidden',
                                                                        overflowY: 'auto',
                                                                        width: 500,
                                                                        maxHeight: 500,
                                                                    }}
                                                                >
                                                                    {dataItem.Description}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </HoverIcon>
                                                )}
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 300,
                                                    marginTop: 10,
                                                }}
                                            >
                                                <span style={{ fontWeight: 500 }}>
                                                    Line Item Notes:
                                                </span>
                                                {dataItem.SpecialInstructions ? (
                                                    <HoverIcon
                                                        growUp={true}
                                                        renderIcon={() => (
                                                            <SpannifyText
                                                                text={dataItem.SpecialInstructions}
                                                                font={''}
                                                                textRows={3}
                                                            >
                                                                <SvgIcon
                                                                    icon={EditPencil}
                                                                    height={12}
                                                                    onClick={() => {
                                                                        setLineItemNotesModalOpen(
                                                                            true
                                                                        )
                                                                    }}
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        paddingLeft: 2,
                                                                        paddingBottom: 2,
                                                                    }}
                                                                />
                                                            </SpannifyText>
                                                        )}
                                                        style={{ display: 'block' }}
                                                    >
                                                        {(closeBubble: () => void) => (
                                                            <div>
                                                                <div
                                                                    className={
                                                                        styles.identifierBubble
                                                                    }
                                                                    style={{
                                                                        overflowX: 'hidden',
                                                                        overflowY: 'auto',
                                                                        width: 500,
                                                                        maxHeight: 500,
                                                                    }}
                                                                >
                                                                    {dataItem.SpecialInstructions!}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </HoverIcon>
                                                ) : (
                                                    <SvgIcon
                                                        icon={EditPencil}
                                                        height={12}
                                                        onClick={() => {
                                                            setLineItemNotesModalOpen(true)
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            paddingLeft: 5,
                                                            paddingBottom: 2,
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* other fields don't have the 5px padding fast quote inputs have */}
                                <div style={{ display: 'flex' }}>
                                    <MoneyInput
                                        rowId={dataItem.rowId}
                                        value={dataItem.UnitPrice}
                                        onChange={(newPrice: number) => {
                                            newPrice !== dataItem.UnitPrice &&
                                                // updateLine({
                                                //     rowId: dataItem.rowId,
                                                //     UnitPrice: newPrice,
                                                // })

                                                dispatch(
                                                    updateLine({
                                                        rowId: dataItem.rowId,
                                                        UnitPrice: newPrice,
                                                    })
                                                )
                                            newPrice === 0
                                                ? setLineItemNotesModalOpen(true)
                                                : setLineItemNotesModalOpen(false)

                                            newPrice >= 0
                                                ? dispatch(
                                                      markRowValid({
                                                          rowId: dataItem.rowId,
                                                          field: 'UnitPrice',
                                                      })
                                                  )
                                                : dispatch(
                                                      markRowInvalid({
                                                          rowId: dataItem.rowId,
                                                          field: 'UnitPrice',
                                                      })
                                                  )

                                            newPrice >= 0
                                                ? dispatch(
                                                      markRowValid({
                                                          rowId: dataItem.rowId,
                                                          field: 'SpecialInstructions',
                                                      })
                                                  )
                                                : dispatch(
                                                      markRowInvalid({
                                                          rowId: dataItem.rowId,
                                                          field: 'SpecialInstructions',
                                                      })
                                                  )
                                        }}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: 50,
                                            marginLeft: 5,
                                        }}
                                    >
                                        <HoverIcon
                                            growLeft
                                            growDown
                                            renderIcon={() => <div className={'a-i-info'} />}
                                        >
                                            {(closeBubble: () => void) =>
                                                //!psLoading &&
                                                //priceStatistics &&
                                                dataItem.metaInfo && (
                                                    <PricePopup
                                                        //priceStatistics={priceStatistics}
                                                        partKey={dataItem.PartKey!}
                                                        priceInfo={dataItem.metaInfo.priceInfo!}
                                                        unitPrice={dataItem.UnitPrice}
                                                        accountCode={accountCode}
                                                        customerType={currentCustomer.CustomerType}
                                                    />
                                                )
                                            }
                                        </HoverIcon>
                                    </div>
                                </div>
                                {/* TODO returnable */}
                                {/* Need Date */}
                                <div style={{ whiteSpace: 'nowrap', position: 'relative' }}>
                                    <DatePicker
                                        minDate={new Date()}
                                        onChange={(date) => {
                                            // updateLine({
                                            //     rowId: dataItem.rowId,
                                            //     OriginalOnDockDate: date
                                            //         ? date.toISOString()
                                            //         : undefined,
                                            // })

                                            dispatch(
                                                updateLine({
                                                    rowId: dataItem.rowId,
                                                    OriginalOnDockDate: date
                                                        ? date.toISOString()
                                                        : undefined,
                                                })
                                            )
                                        }}
                                        value={dataItem.OriginalOnDockDate}
                                        // validityText={dateErrorMessage}
                                        onValidityChange={(valid) =>
                                            valid
                                                ? dispatch(
                                                      markRowValid({
                                                          rowId: dataItem.rowId,
                                                          field: 'date',
                                                      })
                                                  )
                                                : dispatch(
                                                      markRowInvalid({
                                                          rowId: dataItem.rowId,
                                                          field: 'date',
                                                      })
                                                  )
                                        }
                                        style={{ backgroundColor: 'initial' }}
                                    />
                                </div>
                                <div>
                                    {estimatedDelivery && (
                                        <div style={{ display: 'flex' }}>
                                            <AvailabilityInput
                                                initialValue={
                                                    estimatedDelivery.EstimatedDeliveryDate
                                                        ? new Date(
                                                              estimatedDelivery.EstimatedDeliveryDate
                                                          )
                                                        : new Date(
                                                              estimatedDelivery?.EstimatedDeliveryDate
                                                          )
                                                }
                                                value={
                                                    dataItem.ExpectedOnDockDate
                                                        ? new Date(dataItem.ExpectedOnDockDate)
                                                        : null
                                                }
                                                mode={
                                                    availabilityMode === 'D'
                                                        ? 'D'
                                                        : availabilityMode === 'C'
                                                        ? 'C'
                                                        : availabilityMode === 'B'
                                                        ? 'B'
                                                        : 'A'
                                                }
                                                needDate={
                                                    dataItem.OriginalOnDockDate
                                                        ? new Date(dataItem.OriginalOnDockDate)
                                                        : undefined
                                                }
                                                onChange={(mode, value) => {
                                                    if (!value) {
                                                        // updateLine({
                                                        //     rowId: dataItem.rowId,
                                                        //     ExpectedOnDockDate: undefined,
                                                        // })

                                                        dispatch(
                                                            updateLine({
                                                                rowId: dataItem.rowId,
                                                                ExpectedOnDockDate: undefined,
                                                            })
                                                        )
                                                        return
                                                    }
                                                    if (mode === 'D') {
                                                        let weeks = value as number
                                                        if (weeks > 0 && weeks < 1000) {
                                                            const newWorkingDay = addWorkDays(
                                                                new Date(),
                                                                weeks * 5
                                                            )
                                                            if (newWorkingDay) {
                                                                // updateLine({
                                                                //     rowId: dataItem.rowId,
                                                                //     ExpectedOnDockDate:
                                                                //         newWorkingDay.toISOString(),
                                                                // })

                                                                dispatch(
                                                                    updateLine({
                                                                        rowId: dataItem.rowId,
                                                                        ExpectedOnDockDate:
                                                                            newWorkingDay.toISOString(),
                                                                    })
                                                                )
                                                            }
                                                        }
                                                    } else {
                                                        let date = value as Date
                                                        if (isNaN(date.getTime())) {
                                                            return
                                                        }
                                                        // updateLine({
                                                        //     rowId: dataItem.rowId,
                                                        //     ExpectedOnDockDate: date.toISOString(),
                                                        // })

                                                        dispatch(
                                                            updateLine({
                                                                rowId: dataItem.rowId,
                                                                ExpectedOnDockDate:
                                                                    date.toISOString(),
                                                            })
                                                        )
                                                    }
                                                }}
                                                rowId={dataItem.rowId}
                                            />
                                            {estimatedDelivery.EstimatedDeliveryDateDescription && (
                                                <HoverIcon
                                                    growLeft
                                                    renderIcon={() => (
                                                        <span className={'a-i-info'}></span>
                                                    )}
                                                    style={{
                                                        verticalAlign: 'middle',
                                                        marginLeft: 5,
                                                        marginTop: 12,
                                                    }}
                                                >
                                                    {(closeBubble: () => void) => (
                                                        <div
                                                            className={styles.userOverrideBubble}
                                                            style={{
                                                                height: '100%',
                                                                display: 'inline-block',
                                                                textAlign: 'left',
                                                                whiteSpace: 'nowrap',
                                                                width: 'auto',
                                                            }}
                                                            onClick={(e: any) =>
                                                                e.stopPropagation()
                                                            }
                                                        >
                                                            {
                                                                estimatedDelivery.EstimatedDeliveryDateDescription
                                                            }
                                                        </div>
                                                    )}
                                                </HoverIcon>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div style={{ fontSize: 14, fontWeight: 300, textAlign: 'center' }}>
                                    {partsAvailable ? (
                                        <div>
                                            {/* More in stock then needed */}
                                            {partsAvailable.TotalAvailable >=
                                            dataItem.OrderQuantity! ? (
                                                <HoverIcon
                                                    growLeft={true}
                                                    growUp={true}
                                                    renderIcon={() => (
                                                        <div
                                                            onMouseEnter={() =>
                                                                setShowTableBubble(true)
                                                            }
                                                            onMouseLeave={() =>
                                                                setShowTableBubble(false)
                                                            }
                                                        >
                                                            <div>Stock:</div>
                                                            <div style={{ fontWeight: 600 }}>
                                                                {partsAvailable!.TotalAvailable.toLocaleString()}
                                                            </div>
                                                        </div>
                                                    )}
                                                >
                                                    {/* TODO figure out why typescript can't tell not null withoutn the && */}
                                                    {(closeBubble: () => void) =>
                                                        partsAvailable &&
                                                        showTableBubble && (
                                                            <div
                                                                className={styles.tableBubble}
                                                                style={{
                                                                    height: '100%',
                                                                    display: 'flex',
                                                                    textAlign: 'left',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                                onClick={(e: any) =>
                                                                    e.stopPropagation()
                                                                }
                                                            >
                                                                <div style={{ width: '35%' }}>
                                                                    <span
                                                                        style={{ fontWeight: 400 }}
                                                                    >
                                                                        Location #
                                                                    </span>
                                                                    <div
                                                                        className={'a-divider'}
                                                                        style={{
                                                                            marginTop: 10,
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />
                                                                    {partsAvailable.LocationAvailability.map(
                                                                        (location) => (
                                                                            <div>
                                                                                {location.Location}
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        width: '50%',
                                                                        display: 'inline-block',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{ fontWeight: 400 }}
                                                                    >
                                                                        Location
                                                                    </span>
                                                                    <div
                                                                        className={'a-divider'}
                                                                        style={{
                                                                            marginTop: 10,
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />
                                                                    {partsAvailable.LocationAvailability.map(
                                                                        (location) => (
                                                                            <div>
                                                                                {
                                                                                    location.LocationName
                                                                                }
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        width: '15%',
                                                                        textAlign: 'right',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontWeight: 400,
                                                                        }}
                                                                    >
                                                                        Available
                                                                    </span>
                                                                    <div
                                                                        className={'a-divider'}
                                                                        style={{
                                                                            marginTop: 10,
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />
                                                                    {partsAvailable.LocationAvailability.map(
                                                                        (location) => (
                                                                            <div>
                                                                                {location.Available.toLocaleString()}
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                </HoverIcon>
                                            ) : partsAvailable.TotalAvailable === 0 ? (
                                                <>
                                                    <div>Stock:</div>
                                                    <div>
                                                        {partsAvailable.TotalAvailable.toLocaleString()}
                                                    </div>
                                                </>
                                            ) : (
                                                // Some in stock but not enough that is needed
                                                <HoverIcon
                                                    growLeft={true}
                                                    growUp={true}
                                                    renderIcon={() =>
                                                        partsAvailable && (
                                                            <div
                                                                onMouseOver={() =>
                                                                    setShowTableBubble(true)
                                                                }
                                                                onMouseLeave={() =>
                                                                    setShowTableBubble(false)
                                                                }
                                                            >
                                                                <div>Stock:</div>
                                                                <div
                                                                    style={{
                                                                        fontWeight: 300,
                                                                    }}
                                                                >
                                                                    <b>
                                                                        {partsAvailable.TotalAvailable.toLocaleString()}
                                                                    </b>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                >
                                                    {(closeBubble: () => void) =>
                                                        partsAvailable &&
                                                        showTableBubble && (
                                                            <div
                                                                className={styles.tableBubble}
                                                                style={{
                                                                    height: '100%',
                                                                    display: 'flex',
                                                                    textAlign: 'left',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                                onClick={(e: any) =>
                                                                    e.stopPropagation()
                                                                }
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: '30%',
                                                                        textAlign: 'left',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontWeight: 400,
                                                                        }}
                                                                    >
                                                                        Location #
                                                                    </span>
                                                                    <div
                                                                        className={'a-divider'}
                                                                        style={{
                                                                            marginTop: 10,
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />
                                                                    {partsAvailable.LocationAvailability.map(
                                                                        (location) => (
                                                                            <div>
                                                                                {location.Location}
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        width: '55%',
                                                                        display: 'inline-block',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontWeight: 400,
                                                                        }}
                                                                    >
                                                                        Location
                                                                    </span>
                                                                    <div
                                                                        className={'a-divider'}
                                                                        style={{
                                                                            marginTop: 10,
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />
                                                                    {partsAvailable.LocationAvailability.map(
                                                                        (location) => (
                                                                            <div>
                                                                                {
                                                                                    location.LocationName
                                                                                }
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        width: '15%',
                                                                        textAlign: 'right',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontWeight: 400,
                                                                        }}
                                                                    >
                                                                        Available
                                                                    </span>
                                                                    <div
                                                                        className={'a-divider'}
                                                                        style={{
                                                                            marginTop: 10,
                                                                            marginBottom: 10,
                                                                        }}
                                                                    />
                                                                    {partsAvailable.LocationAvailability.map(
                                                                        (location) => (
                                                                            <div>
                                                                                {location.Available.toLocaleString()}
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                </HoverIcon>
                                            )}

                                            {/* None in stock */}
                                            {partsAvailable.TotalAvailable <
                                                dataItem.OrderQuantity! && (
                                                <HoverIcon
                                                    growLeft={true}
                                                    growUp={true}
                                                    renderIcon={() =>
                                                        partsAvailable && (
                                                            <div>
                                                                <div>Lead Time:</div>

                                                                <div style={{ fontWeight: 300 }}>
                                                                    <b>
                                                                        {partsAvailable.LeadTime}{' '}
                                                                        days
                                                                    </b>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                >
                                                    {(closeBubble: () => void) =>
                                                        dataItem.metaInfo?.leadTimeHistory && (
                                                            <div
                                                                className={styles.leadTimeBubble}
                                                                style={{
                                                                    height: '100%',
                                                                    textAlign: 'left',
                                                                    whiteSpace: 'nowrap',
                                                                    padding: '14px 19px',
                                                                }}
                                                                onClick={(e: any) =>
                                                                    e.stopPropagation()
                                                                }
                                                            >
                                                                {dataItem.metaInfo.leadTimeHistory
                                                                    .HistoryTable.length > 0 ? (
                                                                    <span
                                                                        style={{
                                                                            fontWeight: 400,
                                                                            marginRight: 12,
                                                                        }}
                                                                    >
                                                                        Average lead time over the
                                                                        last 90 days is{' '}
                                                                        {
                                                                            dataItem.metaInfo
                                                                                .leadTimeHistory
                                                                                .AverageLeadTime
                                                                        }{' '}
                                                                        days
                                                                    </span>
                                                                ) : (
                                                                    <span
                                                                        style={{
                                                                            fontWeight: 400,
                                                                            marginRight: 12,
                                                                        }}
                                                                    >
                                                                        No lead time history
                                                                    </span>
                                                                )}

                                                                {/* <div>
                                                                    <span
                                                                        style={{
                                                                            fontWeight: 400,
                                                                            marginRight: 12,
                                                                        }}
                                                                    >
                                                                        Average Lead Time
                                                                    </span>
                                                                    {dataItem.metaInfo
                                                                        .leadTimeHistory
                                                                        .HistoryTable.length > 0
                                                                        ? `${dataItem.metaInfo.leadTimeHistory.AverageLeadTime} days`
                                                                        : 'N/A'}
                                                                </div> */}

                                                                {/* <div
                                                                    style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns:
                                                                            '83px 50px 95px 88px 93px 66px',
                                                                        fontWeight: 500,
                                                                        justifyItems: 'end',
                                                                        marginTop: 10,
                                                                    }}
                                                                >
                                                                    <div>PO Number</div>
                                                                    <div>LI #</div>
                                                                    <div>Order Date</div>
                                                                    <div>Lead Time</div>
                                                                    <div>Qty Received</div>
                                                                    <div>Unit Cost</div>
                                                                </div>
                                                                <div
                                                                    className="a-divider"
                                                                    style={{
                                                                        marginTop: 10,
                                                                        marginBottom: 10,
                                                                    }}
                                                                />
                                                                <Scrollbars
                                                                    className={'scroll-bar'}
                                                                    renderTrackHorizontal={() => (
                                                                        <div />
                                                                    )}
                                                                    autoHeight
                                                                    autoHeightMin={0}
                                                                    autoHeightMax={210}
                                                                    // height={210}
                                                                >
                                                                    <>
                                                                        <div
                                                                            style={{
                                                                                display: 'grid',
                                                                                gridTemplateColumns:
                                                                                    '83px 50px 95px 88px 93px 66px',
                                                                                justifyItems: 'end',
                                                                                paddingRight: 15,
                                                                            }}
                                                                        >
                                                                            {dataItem.metaInfo.leadTimeHistory.HistoryTable.map(
                                                                                (row) => (
                                                                                    <>
                                                                                        <div>
                                                                                            {
                                                                                                row.PurchaseOrderNumber
                                                                                            }
                                                                                        </div>
                                                                                        <div>
                                                                                            {
                                                                                                row.LineItem
                                                                                            }
                                                                                        </div>
                                                                                        <div>
                                                                                            {new Date(
                                                                                                row.OrderEntryDate
                                                                                            ).toLocaleDateString()}
                                                                                        </div>
                                                                                        <div>
                                                                                            {
                                                                                                row.LeadTime
                                                                                            }{' '}
                                                                                            Days
                                                                                        </div>
                                                                                        <div>
                                                                                            {
                                                                                                row.QuantityReceived
                                                                                            }
                                                                                        </div>
                                                                                        <div>
                                                                                            {
                                                                                                row.UnitCost
                                                                                            }
                                                                                        </div>
                                                                                    </>
                                                                                )
                                                                            )}
                                                                        </div>
                                                                        {dataItem.metaInfo
                                                                            .leadTimeHistory
                                                                            .HistoryTable.length ===
                                                                            0 && (
                                                                            <div
                                                                                style={{
                                                                                    height: 100,
                                                                                    display: 'flex',
                                                                                    justifyContent:
                                                                                        'center',
                                                                                    alignItems:
                                                                                        'center',
                                                                                }}
                                                                            >
                                                                                This part has not
                                                                                been purchased in
                                                                                the last 90 days.
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                </Scrollbars> */}
                                                            </div>
                                                        )
                                                    }
                                                </HoverIcon>
                                            )}
                                            {(showLimitedAvailability || showConstrained) && (
                                                <HoverIcon
                                                    growDown={true}
                                                    growLeft={true}
                                                    renderIcon={() => (
                                                        <div>
                                                            <span
                                                                className={'a-i-uncertain'}
                                                            ></span>
                                                        </div>
                                                    )}
                                                    style={{ display: 'block' }}
                                                >
                                                    {(closeBubble: () => void) => (
                                                        <div
                                                            className={styles.leadTimeBubble}
                                                            style={{
                                                                height: '100%',
                                                                textAlign: 'left',
                                                                padding: '14px 19px',
                                                                width: 300,
                                                            }}
                                                            onClick={(e: any) =>
                                                                e.stopPropagation()
                                                            }
                                                        >
                                                            {showLimitedAvailability && (
                                                                <div>
                                                                    This product is not the latest
                                                                    and may have limited
                                                                    availability. Please contact the
                                                                    factory.
                                                                </div>
                                                            )}
                                                            {showConstrained && (
                                                                <div>
                                                                    Due to supply constraints,
                                                                    inventory may be reserved for
                                                                    critical needs with contract
                                                                    customers. Please contact the
                                                                    factory.
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </HoverIcon>
                                            )}
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </div>
                                <div style={{ fontWeight: 300, margin: '0px auto', fontSize: 14 }}>
                                    {partInfo?.Returnable === 'Y' ? 'Yes' : ''}
                                    {partInfo?.Returnable === 'N' ? 'No' : ''}
                                    {partInfo?.Returnable === '?' ? '?' : ''}
                                    {partInfo?.Returnable === ' ' ? '?' : ''}
                                </div>
                                <div
                                    style={{
                                        gridRow: '2 / 3',
                                        gridColumn: '5 / 10',
                                        fontWeight: 300,
                                        fontSize: 14,
                                        paddingTop: 25,
                                    }}
                                ></div>

                                <CondensedMetaInfoColumn
                                    dataItem={dataItem}
                                    query={query}
                                    customerType={currentCustomer.CustomerType}
                                />

                                <div
                                    style={{
                                        gridRow: '1 / 3',
                                        gridColumn: '11',
                                        borderLeft: 'solid 1px var(--border1)',
                                        color: '#707070',
                                        backgroundColor: '#e2e2e2',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        paddingLeft: 0,
                                        paddingRight: 0,
                                    }}
                                >
                                    <div
                                        className={styles.lineItemDelete}
                                        onClick={() => setConfirmDelete(dataItem.rowId)}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div />
                                <div />
                                <>
                                    <div
                                        style={{
                                            gridRow: '1 / 3',
                                            gridColumn: '10',
                                            backgroundColor: '#f0f0f0',
                                            borderLeft: '1px solid #737373',
                                        }}
                                    />
                                    <div
                                        style={{
                                            gridRow: '1 / 3',
                                            gridColumn: '11',
                                            backgroundColor: '#e2e2e2',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                        }}
                                    >
                                        {!props.dataItem.last && (
                                            <div
                                                className={styles.lineItemDelete}
                                                onClick={() =>
                                                    dispatch(deleteLine({ rowId: dataItem.rowId }))
                                                }
                                            />
                                        )}
                                    </div>
                                </>
                            </>
                        )}
                    </div>
                </div>
            )}
            {confirmDelete && (
                <ConfirmModal
                    title={`Delete Line ${dataItem.LineItemNumber}?`}
                    text={`Are you sure you want to delete line ${dataItem.LineItemNumber}?`}
                    onCancel={() => setConfirmDelete(null)}
                    onConfirm={() => {
                        dispatch(deleteLine({ rowId: confirmDelete }))
                        setConfirmDelete(null)
                    }}
                />
            )}
        </>
    )
}

// const SectionHeader = () => {
//     return (
//         <div className={styles.sectionHeader} style={{ position: 'relative' }}>
//             <span>Siemens Parts</span>
//             <FastQuoteButton type={'link'} style={{ display: 'inline' }} onClick={() => null}>
//                 Edit
//             </FastQuoteButton>
//             <FastQuoteButton type={'link'} style={{ display: 'inline' }} onClick={() => null}>
//                 Ungroup Section
//             </FastQuoteButton>
//             <div
//                 style={{
//                     position: 'absolute',
//                     left: 1147, // TODO make this width a var
//                     height: '100%',
//                     width: 1,
//                     background: '#737373',
//                 }}
//             >
//                 {' '}
//             </div>
//         </div>
//     )
// }
// const SectionDivider = () => {
//     return <div className={styles.sectionHeader} style={{ padding: 4 }}></div>
// }
interface LineItemHeaderProps {
    reviewingQuoteOutput: boolean
    noNotes?: boolean
}
const LineItemHeader = (props: LineItemHeaderProps) => {
    const {
        reviewingQuoteOutput,
        //noNotes
    } = props

    const dispatch = useAppDispatch()
    const isDateAvailability = useAppSelector(
        (state) => state.order.orderHeader[0].AvailabilityMode !== 'D'
    )

    const availabilityModeHeaderText = () => {
        const availabilityMode = useAppSelector(
            (state) => state.order.orderHeader[0].AvailabilityMode
        )
        return availabilityMode === 'A'
            ? 'Availability (ARO)'
            : availabilityMode === 'B'
            ? 'Balance Ship Date'
            : 'Est. Delivery'
    }

    if (reviewingQuoteOutput) {
        return (
            <div className={clsx([styles.reviewQuoteLineItemHeader])}>
                <div style={{ marginLeft: 11 }}>LI #</div>
                <div style={{ marginLeft: 4 }}>Part # / Mfg. Name / Description</div>
                <div style={{ justifySelf: 'center' }}>Qty.</div>
                <div style={{ justifySelf: 'center' }}>Price</div>
                <div style={{ justifySelf: 'center' }}>Ext. Price</div>
                <div style={{ justifySelf: 'center', whiteSpace: 'nowrap' }}>
                    {availabilityModeHeaderText()}
                </div>
            </div>
        )
    } else {
        return (
            <div className={styles.lineItemHeader}>
                <div></div>
                <div>Part #</div>
                <div>Quantity</div>
                <div>Description</div>
                <div>Price</div>
                <div>Need Date</div>
                <div>
                    <div>{availabilityModeHeaderText()}</div>
                    <div style={{ maxWidth: 100, fontSize: 12 }}>
                        <Switch
                            checkedChildren='Dates'
                            unCheckedChildren='Weeks'
                            checked={isDateAvailability}
                            onChange={(value: boolean) =>
                                dispatch(updateAvailabilityMode(value ? 'C' : 'D'))
                            }
                        />
                    </div>
                </div>
                <div>Availability</div>
                <div>Returns</div>
                {/* TODO make var */}
                <div
                    style={{
                        borderLeft: 'solid 1px #737373',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    Cost
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                ></div>
            </div>
        )
    }
}

function getRebateClassName(RebateStatus: string): string {
    switch (RebateStatus) {
        case 'RED':
            return 'a-i-danger'
        case 'YELLOW':
            return 'a-i-uncertain'
        case 'GREEN':
            return 'a-i-checkmark'
        default:
            return 'infoIcon'
    }
}
